import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/Navbar.jsx';
import Sidebar from '../../components/documentation/Sidebar.jsx';
import RightNav from '../../components/documentation/RightNav.jsx';
import { ArrowLeft, ArrowRight } from '../../assets/index';
import './DirectWebIntegration.css';
import Seo from '../../components/Seo.jsx';


const Endpoint = ({ method = 'POST', path }) => (
    <div className="wq-endpoint break-all text-wrap">
        <span className={`wq-method ${method.toLowerCase()}`}>{method}</span>
        <span className="wq-endpoint-path">{path}</span>
    </div>
);

const Required = () => <span className="wq-required">Required</span>;
const Conditional = () => <span className="wq-cond">Conditional</span>;

const Note = ({ label = 'Heads up', tone, children }) => (
    <div className={`wq-note${tone === 'critical' ? ' wq-critical' : ''}`}>
        {label ? <div className="wq-note-label">{label}</div> : null}
        {children}
    </div>
);

const Flourish = () => <div className="wq-flourish">§ § §</div>;

/* CodeBlock: syntax-colored <pre> with copy button */
const CodeBlock = ({ children }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async (e) => {
        const pre = e.currentTarget.parentElement.querySelector('pre');
        if (!pre) return;
        await navigator.clipboard.writeText(pre.innerText).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        });
    };

    return (
        <div className="wq-code-wrap">
            <button
                className={`wq-copy-btn ${copied ? 'copied' : ''}`}
                onClick={handleCopy}
                type="button"
            >
                {copied ? 'Copied' : 'Copy'}
            </button>
            <pre>{children}</pre>
        </div>
    );
};

/* Tabs: simple controlled tab system */
const Tabs = ({ tabs, defaultTab }) => {
    const [active, setActive] = useState(defaultTab || tabs[0].id);

    return (
        <div className="wq-tabs">
            <div className="wq-tab-buttons">
                {tabs.map((t) => (
                    <button
                        key={t.id}
                        type="button"
                        className={`wq-tab-btn ${active === t.id ? 'active' : ''}`}
                        onClick={() => setActive(t.id)}
                    >
                        {t.label}
                    </button>
                ))}
            </div>
            {tabs.map((t) => (
                <div
                    key={t.id}
                    className={`wq-tab-content ${active === t.id ? 'active' : ''}`}
                >
                    {t.content}
                </div>
            ))}
        </div>
    );
};

/* Shorthand spans for syntax highlighting inside <pre> */
const kw = (s) => <span className="wq-kw">{s}</span>;
const str = (s) => <span className="wq-str">{s}</span>;
const num = (s) => <span className="wq-num">{s}</span>;
const com = (s) => <span className="wq-com">{s}</span>;

/* Build a 7-language tab set in a consistent order */
const langTabs = (prefix, snips) => [
    { id: `${prefix}-curl`, label: 'cURL', content: snips.curl },
    { id: `${prefix}-node`, label: 'Node.js', content: snips.node },
    { id: `${prefix}-java`, label: 'Java Spring Boot', content: snips.java },
    { id: `${prefix}-php`, label: 'PHP', content: snips.php },
    { id: `${prefix}-csharp`, label: 'C# / .NET', content: snips.csharp },
    { id: `${prefix}-go`, label: 'Go', content: snips.go },
    { id: `${prefix}-laravel`, label: 'Laravel', content: snips.laravel },
];

const BASE_URL = 'https://services.wayapay.ng/merchant-middleware';

/* ============================================================
   SETUP: reusable client per language
   (Authorization: Bearer {key} + X-Merchant-Id)
   ============================================================ */

const setupNode = (
    <CodeBlock>
{com('// npm install axios')}{'\n\n'}
{com('// wayapay.js')}{'\n'}
{kw('const')} axios = {kw('require')}({str("'axios'")});{'\n\n'}
{kw('class')} WayaPay {'{\n'}
{'  '}{kw('constructor')}({'{ merchantId, secretKey, baseUrl = '}{str(`'${BASE_URL}'`)}{' }'}) {'{\n'}
{'    '}{kw('this')}.client = axios.create({'{\n'}
{'      baseURL: baseUrl,\n'}
{'      headers: {\n'}
{'        '}{str("'Authorization'")}: {str('`Bearer ')}${'{secretKey}'}{str('`')},{'\n'}
{'        '}{str("'X-Merchant-Id'")}: merchantId,{'\n'}
{'        '}{str("'Content-Type'")}: {str("'application/json'")}{'\n'}
{'      },\n'}
{'      timeout: '}{num('30000')}{'\n'}
{'    });\n'}
{'  }\n'}
{'}\n\n'}
module.exports = WayaPay;{'\n\n'}
{com('// usage')}{'\n'}
{kw('const')} waya = {kw('new')} WayaPay({'{\n'}
{'  merchantId: process.env.WAYA_MERCHANT_ID,\n'}
{'  secretKey: process.env.WAYA_SECRET_KEY\n'}
{'});'}
    </CodeBlock>
);

const setupJava = (
    <CodeBlock>
{com('// build.gradle')}{'\n'}
{com("// implementation 'org.springframework.boot:spring-boot-starter-webflux'")}{'\n\n'}
{kw('package')} com.yourapp.wayapay;{'\n\n'}
{kw('import')} org.springframework.beans.factory.annotation.Value;{'\n'}
{kw('import')} org.springframework.stereotype.Component;{'\n'}
{kw('import')} org.springframework.web.reactive.function.client.WebClient;{'\n\n'}
{'@Component\n'}
{kw('public class')}{' WayaPayClient {'}{'\n\n'}
{'    '}{kw('private final')}{' WebClient client;'}{'\n\n'}
{'    '}{kw('public')}{' WayaPayClient(\n'}
{'            @Value('}{str(`"\${wayapay.merchant-id}"`)}{') String merchantId,\n'}
{'            @Value('}{str(`"\${wayapay.secret-key}"`)}{') String secretKey,\n'}
{'            @Value('}{str(`"\${wayapay.base-url:${BASE_URL}}"`)}{') String baseUrl) {\n\n'}
{'        '}{kw('this')}{'.client = WebClient.builder()\n'}
{'            .baseUrl(baseUrl)\n'}
{'            .defaultHeader('}{str('"Authorization"')}{', '}{str('"Bearer "')}{' + secretKey)\n'}
{'            .defaultHeader('}{str('"X-Merchant-Id"')}{', merchantId)\n'}
{'            .defaultHeader('}{str('"Content-Type"')}{', '}{str('"application/json"')}{')\n'}
{'            .build();\n'}
{'    }\n\n'}
{'    '}{kw('public')}{' WebClient getClient() { '}{kw('return')}{' client; }\n'}
{'}'}
    </CodeBlock>
);


const setupPhp = (
    <CodeBlock>
{com('// composer require guzzlehttp/guzzle')}{'\n\n'}
{'<?php\n'}
{kw('namespace')} App\\WayaPay;{'\n\n'}
{kw('use')} GuzzleHttp\\Client;{'\n\n'}
{kw('class')}{' WayaPay\n'}
{'{\n'}
{'    '}{kw('private')}{' Client $client;\n\n'}
{'    '}{kw('public function')}{' __construct('}{kw('string')}{' $merchantId, '}{kw('string')}{' $secretKey, '}{kw('string')}{' $baseUrl = '}{str(`'${BASE_URL}'`)}{')\n'}
{'    {\n'}
{'        $this->client = '}{kw('new')}{' Client([\n'}
{'            '}{str("'base_uri'")}{' => $baseUrl,\n'}
{'            '}{str("'timeout'")}{'  => '}{num('30')}{',\n'}
{'            '}{str("'headers'")}{'  => [\n'}
{'                '}{str("'Authorization'")}{' => '}{str("'Bearer '")}{' . $secretKey,\n'}
{'                '}{str("'X-Merchant-Id'")}{' => $merchantId,\n'}
{'                '}{str("'Content-Type'")}{'  => '}{str("'application/json'")}{'\n'}
{'            ]\n'}
{'        ]);\n'}
{'    }\n\n'}
{'    '}{kw('public function')}{' client(): Client { '}{kw('return')}{' $this->client; }\n'}
{'}'}
    </CodeBlock>
);

const setupCsharp = (
    <CodeBlock>
{com('// dotnet add package System.Net.Http.Json')}{'\n\n'}
{kw('using')} System.Net.Http.Headers;{'\n'}
{kw('using')} System.Net.Http.Json;{'\n\n'}
{kw('public class')}{' WayaPayClient\n'}
{'{\n'}
{'    '}{kw('private readonly')}{' HttpClient _http;\n\n'}
{'    '}{kw('public')}{' WayaPayClient('}{kw('string')}{' merchantId, '}{kw('string')}{' secretKey,\n'}
{'                         '}{kw('string')}{' baseUrl = '}{str(`"${BASE_URL}"`)}{')\n'}
{'    {\n'}
{'        _http = '}{kw('new')}{' HttpClient { BaseAddress = '}{kw('new')}{' Uri(baseUrl) };\n'}
{'        _http.DefaultRequestHeaders.Authorization =\n'}
{'            '}{kw('new')}{' AuthenticationHeaderValue('}{str('"Bearer"')}{', secretKey);\n'}
{'        _http.DefaultRequestHeaders.Add('}{str('"X-Merchant-Id"')}{', merchantId);\n'}
{'        _http.DefaultRequestHeaders.Accept.Add(\n'}
{'            '}{kw('new')}{' MediaTypeWithQualityHeaderValue('}{str('"application/json"')}{'));\n'}
{'    }\n\n'}
{'    '}{kw('public')}{' HttpClient Http => _http;\n'}
{'}'}
    </CodeBlock>
);

const setupGo = (
    <CodeBlock>
{com('// go get github.com/go-resty/resty/v2')}{'\n\n'}
{kw('package')} wayapay{'\n\n'}
{kw('import')} ({'\n'}
{'    '}{str('"time"')}{'\n'}
{'    '}{str('"github.com/go-resty/resty/v2"')}{'\n'}
{')\n\n'}
{kw('type')}{' Client '}{kw('struct')}{' {\n'}
{'    http *resty.Client\n'}
{'}\n\n'}
{kw('func')}{' New(merchantID, secretKey '}{kw('string')}{') *Client {\n'}
{'    c := resty.New().\n'}
{'        SetBaseURL('}{str(`"${BASE_URL}"`)}{').\n'}
{'        SetTimeout('}{num('30')}{' * time.Second).\n'}
{'        SetHeader('}{str('"Authorization"')}{', '}{str('"Bearer "')}{'+secretKey).\n'}
{'        SetHeader('}{str('"X-Merchant-Id"')}{', merchantID).\n'}
{'        SetHeader('}{str('"Content-Type"')}{', '}{str('"application/json"')}{')\n\n'}
{'    '}{kw('return')}{' &Client{http: c}\n'}
{'}'}
    </CodeBlock>
);

const setupLaravel = (
    <CodeBlock>
{com('// config/wayapay.php')}{'\n'}
{'<?php\n'}
{kw('return')}{' [\n'}
{'    '}{str("'merchant_id'")}{' => env('}{str("'WAYAPAY_MERCHANT_ID'")}{'),\n'}
{'    '}{str("'secret_key'")}{'  => env('}{str("'WAYAPAY_SECRET_KEY'")}{'),\n'}
{'    '}{str("'base_url'")}{'    => env('}{str("'WAYAPAY_BASE_URL'")}{', '}{str(`'${BASE_URL}'`)}{'),\n'}
{'];\n\n'}
{com('// app/Services/WayaPayService.php')}{'\n'}
{'<?php\n'}
{kw('namespace')} App\\Services;{'\n\n'}
{kw('use')} Illuminate\\Support\\Facades\\Http;{'\n'}
{kw('use')} Illuminate\\Http\\Client\\PendingRequest;{'\n\n'}
{kw('class')}{' WayaPayService\n'}
{'{\n'}
{'    '}{kw('public function')}{' client(): PendingRequest\n'}
{'    {\n'}
{'        '}{kw('return')}{' Http::baseUrl(config('}{str("'wayapay.base_url'")}{'))\n'}
{'            ->timeout('}{num('30')}{')\n'}
{'            ->withToken(config('}{str("'wayapay.secret_key'")}{'))\n'}
{'            ->withHeaders(['}{str("'X-Merchant-Id'")}{' => config('}{str("'wayapay.merchant_id'")}{')])\n'}
{'            ->asJson();\n'}
{'    }\n'}
{'}'}
    </CodeBlock>
);

const setupCurl = (
    <CodeBlock>
{com('# Set your credentials as environment variables')}{'\n'}
{kw('export')} WAYA_MERCHANT_ID={str('"MER_LOfV71779980928158w7MEX"')}{'\n'}
{kw('export')} WAYA_SECRET_KEY={str('"WAYASECK_TEST_0x..."')}{'\n'}
{kw('export')} WAYA_BASE={str(`"${BASE_URL}"`)}{'\n\n'}
{com('# Send both credentials on every request')}{'\n'}
{'curl -H '}{str('"Authorization: Bearer $WAYA_SECRET_KEY"')} {'\\\n'}
{'     -H '}{str('"X-Merchant-Id: $WAYA_MERCHANT_ID"')} {'\\\n'}
{'     -H '}{str('"Content-Type: application/json"')} {'\\\n'}
{'     $WAYA_BASE/api/v2/get-bank-list'}
    </CodeBlock>
);

/* ============================================================
   Response envelope examples
   ============================================================ */

const responseEnvelope = (
    <CodeBlock>
{'{ '}{str('"success"')}: {kw('true')}, {str('"code"')}: {str('"00"')}, {str('"message"')}: {str('"..."')}, {str('"data"')}: {'{ }'}, {str('"timestamp"')}: {str('"..."')}{' }'}
    </CodeBlock>
);

const responseErrorEnvelope = (
    <CodeBlock>
{'{\n'}
{'  '}{str('"success"')}: {kw('false')},{'\n'}
{'  '}{str('"code"')}: {str('"13"')},{'\n'}
{'  '}{str('"message"')}: {str('"amount: must be greater than zero"')},{'\n'}
{'  '}{str('"data"')}: {kw('null')},{'\n'}
{'  '}{str('"timestamp"')}: {str('"..."')}{'\n'}
{'}'}
    </CodeBlock>
);

/* ============================================================
   PAYOUT - Get bank list  (GET /api/v2/get-bank-list)
   ============================================================ */

const banksNode = (
    <CodeBlock>
{kw('async')} fetchBanks() {'{\n'}
{'  '}{kw('const')} {'{ data } = '}{kw('await this')}.client.get({str("'/api/v2/get-bank-list'")});{'\n'}
{'  '}{kw('return')} data.data; {com('// array of banks')}{'\n'}
{'}\n\n'}
{com('// usage')}{'\n'}
{kw('const')} banks = {kw('await')} waya.fetchBanks();{'\n'}
banks.forEach(b {'=>'} console.log({str('`')}${'{b.code}'}{str(' - ')}${'{b.name}'}{str('`')}));
    </CodeBlock>
);

const banksJava = (
    <CodeBlock>
{kw('public')}{' Mono<Map> fetchBanks() {'}{'\n'}
{'    '}{kw('return')}{' client.get()\n'}
{'        .uri('}{str('"/api/v2/get-bank-list"')}{')\n'}
{'        .retrieve()\n'}
{'        .bodyToMono(Map.class);\n'}
{'}'}
    </CodeBlock>
);

const banksPhp = (
    <CodeBlock>
{kw('public function')}{' fetchBanks(): '}{kw('array')}{'\n'}
{'{\n'}
{'    $response = $this->client->get('}{str("'/api/v2/get-bank-list'")}{');\n'}
{'    '}{kw('return')}{' json_decode($response->getBody()->getContents(), '}{kw('true')}{');\n'}
{'}'}
    </CodeBlock>
);

const banksCsharp = (
    <CodeBlock>
{kw('public async')}{' Task<JsonElement> FetchBanksAsync()\n'}
{'{\n'}
{'    '}{kw('return await')}{' _http.GetFromJsonAsync<JsonElement>('}{str('"/api/v2/get-bank-list"')}{');\n'}
{'}'}
    </CodeBlock>
);

const banksGo = (
    <CodeBlock>
{kw('func')}{' (c *Client) FetchBanks() (map[string]interface{}, error) {'}{'\n'}
{'    '}{kw('var')}{' result map[string]interface{}\n'}
{'    _, err := c.http.R().SetResult(&result).Get('}{str('"/api/v2/get-bank-list"')}{')\n'}
{'    '}{kw('return')}{' result, err\n'}
{'}'}
    </CodeBlock>
);

const banksLaravel = (
    <CodeBlock>
{kw('public function')}{' fetchBanks(): '}{kw('array')}{'\n'}
{'{\n'}
{'    '}{kw('return')}{' $this->client()->get('}{str("'/api/v2/get-bank-list'")}{')->throw()->json();\n'}
{'}'}
    </CodeBlock>
);

const banksCurl = (
    <CodeBlock>
{'curl -X GET \\\n'}
{'  '}{str("'$WAYA_BASE/api/v2/get-bank-list'")} {'\\\n'}
{'  -H '}{str("'Authorization: Bearer $WAYA_SECRET_KEY'")} {'\\\n'}
{'  -H '}{str("'X-Merchant-Id: $WAYA_MERCHANT_ID'")}
    </CodeBlock>
);

const banksResponse = (
    <CodeBlock>
{'{\n'}
{'  '}{str('"success"')}: {kw('true')},{'\n'}
{'  '}{str('"code"')}: {str('"00"')},{'\n'}
{'  '}{str('"message"')}: {str('"Bank list retrieved"')},{'\n'}
{'  '}{str('"data"')}: [{'\n'}
{'    { '}{str('"code"')}: {str('"044"')}, {str('"name"')}: {str('"Access Bank"')}, {str('"id"')}: {str('"1"')}, {str('"status"')}: {kw('true')} {'},\n'}
{'    { '}{str('"code"')}: {str('"058"')}, {str('"name"')}: {str('"GTBank"')},      {str('"id"')}: {str('"2"')}, {str('"status"')}: {kw('true')} {'}\n'}
{'  ]\n'}
{'}'}
    </CodeBlock>
);

/* ============================================================
   PAYOUT - Account verification  (POST /api/v2/verify-account)
   ============================================================ */

const verifyAcctNode = (
    <CodeBlock>
{kw('async')} verifyAccount(accountNumber, bankCode, enquiryType = {str("'OTHERS'")}) {'{\n'}
{'  '}{kw('const')} {'{ data } = '}{kw('await this')}.client.post({str("'/api/v2/verify-account'")}, {'{\n'}
{'    accountNumber,\n'}
{'    bankCode,\n'}
{'    enquiryType\n'}
{'  });\n'}
{'  '}{kw('return')} data;{'\n'}
{'}\n\n'}
{com('// usage')}{'\n'}
{kw('const')} resolved = {kw('await')} waya.verifyAccount({str("'0123456789'")}, {str("'044'")});{'\n'}
console.log(resolved.data.accountName); {com('// "JOHN ADEKUNLE DOE"')}
    </CodeBlock>
);

const verifyAcctJava = (
    <CodeBlock>
{kw('public')}{' Mono<Map> verifyAccount(String accountNumber, String bankCode, String enquiryType) {'}{'\n'}
{'    '}{kw('return')}{' client.post()\n'}
{'        .uri('}{str('"/api/v2/verify-account"')}{')\n'}
{'        .bodyValue(Map.of(\n'}
{'            '}{str('"accountNumber"')}{', accountNumber,\n'}
{'            '}{str('"bankCode"')}{', bankCode,\n'}
{'            '}{str('"enquiryType"')}{', enquiryType\n'}
{'        ))\n'}
{'        .retrieve()\n'}
{'        .bodyToMono(Map.class);\n'}
{'}'}
    </CodeBlock>
);

const verifyAcctPhp = (
    <CodeBlock>
{kw('public function')}{' verifyAccount('}{kw('string')}{' $accountNumber, '}{kw('string')}{' $bankCode, '}{kw('string')}{' $enquiryType = '}{str("'OTHERS'")}{'): '}{kw('array')}{'\n'}
{'{\n'}
{'    $response = $this->client->post('}{str("'/api/v2/verify-account'")}{', [\n'}
{'        '}{str("'json'")}{' => [\n'}
{'            '}{str("'accountNumber'")}{' => $accountNumber,\n'}
{'            '}{str("'bankCode'")}{'      => $bankCode,\n'}
{'            '}{str("'enquiryType'")}{'   => $enquiryType\n'}
{'        ]\n'}
{'    ]);\n\n'}
{'    '}{kw('return')}{' json_decode($response->getBody()->getContents(), '}{kw('true')}{');\n'}
{'}'}
    </CodeBlock>
);

const verifyAcctCsharp = (
    <CodeBlock>
{kw('public async')}{' Task<JsonElement> VerifyAccountAsync('}{kw('string')}{' accountNumber, '}{kw('string')}{' bankCode, '}{kw('string')}{' enquiryType = '}{str('"OTHERS"')}{')\n'}
{'{\n'}
{'    '}{kw('var')}{' body = '}{kw('new')}{' { accountNumber, bankCode, enquiryType };\n'}
{'    '}{kw('var')}{' response = '}{kw('await')}{' _http.PostAsJsonAsync('}{str('"/api/v2/verify-account"')}{', body);\n'}
{'    response.EnsureSuccessStatusCode();\n'}
{'    '}{kw('return await')}{' response.Content.ReadFromJsonAsync<JsonElement>();\n'}
{'}'}
    </CodeBlock>
);

const verifyAcctGo = (
    <CodeBlock>
{kw('func')}{' (c *Client) VerifyAccount(accountNumber, bankCode, enquiryType '}{kw('string')}{') (map[string]interface{}, error) {'}{'\n'}
{'    body := map[string]string{\n'}
{'        '}{str('"accountNumber"')}{': accountNumber,\n'}
{'        '}{str('"bankCode"')}{':      bankCode,\n'}
{'        '}{str('"enquiryType"')}{':   enquiryType,\n'}
{'    }\n\n'}
{'    '}{kw('var')}{' result map[string]interface{}\n'}
{'    _, err := c.http.R().SetBody(body).SetResult(&result).\n'}
{'        Post('}{str('"/api/v2/verify-account"')}{')\n'}
{'    '}{kw('return')}{' result, err\n'}
{'}'}
    </CodeBlock>
);

const verifyAcctLaravel = (
    <CodeBlock>
{kw('public function')}{' verifyAccount('}{kw('string')}{' $accountNumber, '}{kw('string')}{' $bankCode, '}{kw('string')}{' $enquiryType = '}{str("'OTHERS'")}{'): '}{kw('array')}{'\n'}
{'{\n'}
{'    '}{kw('return')}{' $this->client()\n'}
{'        ->post('}{str("'/api/v2/verify-account'")}{', [\n'}
{'            '}{str("'accountNumber'")}{' => $accountNumber,\n'}
{'            '}{str("'bankCode'")}{'      => $bankCode,\n'}
{'            '}{str("'enquiryType'")}{'   => $enquiryType,\n'}
{'        ])\n'}
{'        ->throw()\n'}
{'        ->json();\n'}
{'}'}
    </CodeBlock>
);

const verifyAcctCurl = (
    <CodeBlock>
{'curl -X POST \\\n'}
{'  '}{str("'$WAYA_BASE/api/v2/verify-account'")} {'\\\n'}
{'  -H '}{str("'Authorization: Bearer $WAYA_SECRET_KEY'")} {'\\\n'}
{'  -H '}{str("'X-Merchant-Id: $WAYA_MERCHANT_ID'")} {'\\\n'}
{'  -H '}{str("'Content-Type: application/json'")} {'\\\n'}
{'  -d '}{str(`'{ "accountNumber": "0123456789", "bankCode": "044", "enquiryType": "OTHERS" }'`)}
    </CodeBlock>
);

const verifyAcctRequest = (
    <CodeBlock>
{'{ '}{str('"accountNumber"')}: {str('"0123456789"')}, {str('"bankCode"')}: {str('"044"')}, {str('"enquiryType"')}: {str('"OTHERS"')}{' }'}
    </CodeBlock>
);

const verifyAcctResponse = (
    <CodeBlock>
{'{\n'}
{'  '}{str('"success"')}: {kw('true')},{'\n'}
{'  '}{str('"code"')}: {str('"00"')},{'\n'}
{'  '}{str('"message"')}: {str('"Account verification completed"')},{'\n'}
{'  '}{str('"data"')}: {'{\n'}
{'    '}{str('"successful"')}: {kw('true')},{'\n'}
{'    '}{str('"responseCode"')}: {str('"00"')},{'\n'}
{'    '}{str('"responseMessage"')}: {str('"Approved"')},{'\n'}
{'    '}{str('"accountNumber"')}: {str('"0123456789"')},{'\n'}
{'    '}{str('"accountName"')}: {str('"JOHN ADEKUNLE DOE"')},{'\n'}
{'    '}{str('"bankCode"')}: {str('"044"')},{'\n'}
{'    '}{str('"bankName"')}: {str('"Access Bank"')},{'\n'}
{'    '}{str('"enquiryType"')}: {str('"OTHERS"')}{'\n'}
{'  }\n'}
{'}'}
    </CodeBlock>
);

/* ============================================================
   PAYOUT - Initiate payout  (POST /api/v2/payment-payout/initiate)
   ============================================================ */

const payoutNode = (
    <CodeBlock>
{kw('async')} initiatePayout({'{ amount, currency, accountNumber, bankCode, accountName, reference, narration }'}) {'{\n'}
{'  '}{kw('const')} {'{ data } = '}{kw('await this')}.client.post({str("'/api/v2/payment-payout/initiate'")}, {'{\n'}
{'    amount, currency, accountNumber, bankCode, accountName, reference, narration\n'}
{'  });\n'}
{'  '}{kw('return')} data;{'\n'}
{'}\n\n'}
{com('// usage')}{'\n'}
{kw('const')} payout = {kw('await')} waya.initiatePayout({'{\n'}
{'  amount: '}{num('25000.00')},{'\n'}
{'  currency: '}{str("'NGN'")},{'\n'}
{'  accountNumber: '}{str("'0123456789'")},{'\n'}
{'  bankCode: '}{str("'044'")},{'\n'}
{'  accountName: '}{str("'JOHN ADEKUNLE DOE'")},{'\n'}
{'  reference: '}{str("'PAYOUT-20260604-001'")}{'\n'}
{'});\n\n'}
console.log(payout.data.payoutReference);
    </CodeBlock>
);

const payoutJava = (
    <CodeBlock>
{kw('public')}{' Mono<Map> initiatePayout(Map<String, Object> payload) {'}{'\n'}
{'    '}{kw('return')}{' client.post()\n'}
{'        .uri('}{str('"/api/v2/payment-payout/initiate"')}{')\n'}
{'        .bodyValue(payload)\n'}
{'        .retrieve()\n'}
{'        .bodyToMono(Map.class);\n'}
{'}'}
    </CodeBlock>
);

const payoutPhp = (
    <CodeBlock>
{kw('public function')}{' initiatePayout('}{kw('array')}{' $payload): '}{kw('array')}{'\n'}
{'{\n'}
{'    $response = $this->client->post('}{str("'/api/v2/payment-payout/initiate'")}{', [\n'}
{'        '}{str("'json'")}{' => $payload\n'}
{'    ]);\n'}
{'    '}{kw('return')}{' json_decode($response->getBody()->getContents(), '}{kw('true')}{');\n'}
{'}'}
    </CodeBlock>
);

const payoutCsharp = (
    <CodeBlock>
{kw('public async')}{' Task<JsonElement> InitiatePayoutAsync('}{kw('object')}{' payload)\n'}
{'{\n'}
{'    '}{kw('var')}{' response = '}{kw('await')}{' _http.PostAsJsonAsync('}{str('"/api/v2/payment-payout/initiate"')}{', payload);\n'}
{'    response.EnsureSuccessStatusCode();\n'}
{'    '}{kw('return await')}{' response.Content.ReadFromJsonAsync<JsonElement>();\n'}
{'}'}
    </CodeBlock>
);

const payoutGo = (
    <CodeBlock>
{kw('func')}{' (c *Client) InitiatePayout(payload map[string]interface{}) (map[string]interface{}, error) {'}{'\n'}
{'    '}{kw('var')}{' result map[string]interface{}\n'}
{'    _, err := c.http.R().SetBody(payload).SetResult(&result).\n'}
{'        Post('}{str('"/api/v2/payment-payout/initiate"')}{')\n'}
{'    '}{kw('return')}{' result, err\n'}
{'}'}
    </CodeBlock>
);

const payoutLaravel = (
    <CodeBlock>
{kw('public function')}{' initiatePayout('}{kw('array')}{' $payload): '}{kw('array')}{'\n'}
{'{\n'}
{'    '}{kw('return')}{' $this->client()\n'}
{'        ->post('}{str("'/api/v2/payment-payout/initiate'")}{', $payload)\n'}
{'        ->throw()\n'}
{'        ->json();\n'}
{'}'}
    </CodeBlock>
);

const payoutCurl = (
    <CodeBlock>
{'curl -X POST \\\n'}
{'  '}{str("'$WAYA_BASE/api/v2/payment-payout/initiate'")} {'\\\n'}
{'  -H '}{str("'Authorization: Bearer $WAYA_SECRET_KEY'")} {'\\\n'}
{'  -H '}{str("'X-Merchant-Id: $WAYA_MERCHANT_ID'")} {'\\\n'}
{'  -H '}{str("'Content-Type: application/json'")} {'\\\n'}
{'  -d '}{str(`'{
        "amount": 25000.00, "currency": "NGN",
        "accountNumber": "0123456789", "bankCode": "044",
        "accountName": "JOHN ADEKUNLE DOE", "reference": "PAYOUT-20260604-001"
      }'`)}
    </CodeBlock>
);

const payoutRequest = (
    <CodeBlock>
{'{\n'}
{'  '}{str('"amount"')}: {num('25000.00')},{'\n'}
{'  '}{str('"currency"')}: {str('"NGN"')},{'\n'}
{'  '}{str('"accountNumber"')}: {str('"0123456789"')},{'\n'}
{'  '}{str('"bankCode"')}: {str('"044"')},{'\n'}
{'  '}{str('"accountName"')}: {str('"JOHN ADEKUNLE DOE"')},{'\n'}
{'  '}{str('"reference"')}: {str('"PAYOUT-20260604-001"')},{'\n'}
{'  '}{str('"narration"')}: {str('"Payout for order 1234"')}{'\n'}
{'}'}
    </CodeBlock>
);

const payoutResponse = (
    <CodeBlock>
{'{\n'}
{'  '}{str('"success"')}: {kw('true')},{'\n'}
{'  '}{str('"code"')}: {str('"00"')},{'\n'}
{'  '}{str('"message"')}: {str('"Payout accepted for processing"')},{'\n'}
{'  '}{str('"data"')}: {'{\n'}
{'    '}{str('"payoutReference"')}: {str('"MPO-20260604-abc123"')},{'\n'}
{'    '}{str('"merchantReference"')}: {str('"PAYOUT-20260604-001"')},{'\n'}
{'    '}{str('"status"')}: {str('"PROCESSING"')},{'\n'}
{'    '}{str('"message"')}: {str('"Payout accepted for processing"')}{'\n'}
{'  }\n'}
{'}'}
    </CodeBlock>
);

const payoutStatusResponse = (
    <CodeBlock>
{'{\n'}
{'  '}{str('"success"')}: {kw('true')},{'\n'}
{'  '}{str('"code"')}: {str('"00"')},{'\n'}
{'  '}{str('"message"')}: {str('"Payout status retrieved"')},{'\n'}
{'  '}{str('"data"')}: {'{\n'}
{'    '}{str('"transactionReference"')}: {str('"PAYOUT-20260604-001"')},{'\n'}
{'    '}{str('"status"')}: {str('"SUCCESS"')},{'\n'}
{'    '}{str('"amount"')}: {str('"500.00"')},{'\n'}
{'    '}{str('"destinationAccountNumber"')}: {str('"0123456789"')},{'\n'}
{'    '}{str('"destinationAccountName"')}: {str('"JOHN DOE"')},{'\n'}
{'    '}{str('"destinationBankName"')}: {str('"GTBank"')},{'\n'}
{'    '}{str('"narration"')}: {str('"TRF FM ... REF:PAYOUT-20260604-001"')},{'\n'}
{'    '}{str('"createdAt"')}: {str('"2026-06-04T10:00:32"')}{'\n'}
{'  }\n'}
{'}'}
    </CodeBlock>
);

const payoutStatusCurl = (
    <CodeBlock>
{'curl -X GET \\\n'}
{'  '}{str("'$WAYA_BASE/api/v2/payment-payout/status/PAYOUT-20260604-001'")} {'\\\n'}
{'  -H '}{str("'Authorization: Bearer $WAYA_SECRET_KEY'")} {'\\\n'}
{'  -H '}{str("'X-Merchant-Id: $WAYA_MERCHANT_ID'")}
    </CodeBlock>
);


/* ============================================================
   BVN VERIFICATION - two-step OTP flow
   Step 1: POST /api/v2/identity-verification/bvn/initiate
   Step 2: POST /api/v2/identity-verification/bvn/complete
   ============================================================ */

/* ---------- Step 1: initiate ---------- */

const bvnInitiateNode = (
    <CodeBlock>
{kw('async')} initiateBvn(bvn) {'{\n'}
{'  '}{kw('const')} {'{ data } = '}{kw('await this')}.client.post({str("'/api/v2/identity-verification/bvn/initiate'")}, {'{ bvn }'});{'\n'}
{'  '}{kw('return')} data;{'\n'}
{'}\n\n'}
{com('// usage')}{'\n'}
{kw('const')} started = {kw('await')} waya.initiateBvn({str("'22222222222'")});{'\n'}
{com('// keep the reference on your server, never in a URL or browser storage')}{'\n'}
console.log(started.data.reference, started.data.phoneNumber);
    </CodeBlock>
);

const bvnInitiateJava = (
    <CodeBlock>
{kw('public')}{' Mono<Map> initiateBvn(String bvn) {'}{'\n'}
{'    '}{kw('return')}{' client.post()\n'}
{'        .uri('}{str('"/api/v2/identity-verification/bvn/initiate"')}{')\n'}
{'        .bodyValue(Map.of('}{str('"bvn"')}{', bvn))\n'}
{'        .retrieve()\n'}
{'        .bodyToMono(Map.class);\n'}
{'}'}
    </CodeBlock>
);

const bvnInitiatePhp = (
    <CodeBlock>
{kw('public function')}{' initiateBvn('}{kw('string')}{' $bvn): '}{kw('array')}{'\n'}
{'{\n'}
{'    $response = $this->client->post('}{str("'/api/v2/identity-verification/bvn/initiate'")}{', [\n'}
{'        '}{str("'json'")}{' => ['}{str("'bvn'")}{' => $bvn]\n'}
{'    ]);\n'}
{'    '}{kw('return')}{' json_decode($response->getBody()->getContents(), '}{kw('true')}{');\n'}
{'}'}
    </CodeBlock>
);

const bvnInitiateCsharp = (
    <CodeBlock>
{kw('public async')}{' Task<JsonElement> InitiateBvnAsync('}{kw('string')}{' bvn)\n'}
{'{\n'}
{'    '}{kw('var')}{' response = '}{kw('await')}{' _http.PostAsJsonAsync('}{str('"/api/v2/identity-verification/bvn/initiate"')}{', '}{kw('new')}{' { bvn });\n'}
{'    response.EnsureSuccessStatusCode();\n'}
{'    '}{kw('return await')}{' response.Content.ReadFromJsonAsync<JsonElement>();\n'}
{'}'}
    </CodeBlock>
);

const bvnInitiateGo = (
    <CodeBlock>
{kw('func')}{' (c *Client) InitiateBvn(bvn '}{kw('string')}{') (map[string]interface{}, error) {'}{'\n'}
{'    '}{kw('var')}{' result map[string]interface{}\n'}
{'    _, err := c.http.R().\n'}
{'        SetBody(map[string]string{'}{str('"bvn"')}{': bvn}).\n'}
{'        SetResult(&result).\n'}
{'        Post('}{str('"/api/v2/identity-verification/bvn/initiate"')}{')\n'}
{'    '}{kw('return')}{' result, err\n'}
{'}'}
    </CodeBlock>
);

const bvnInitiateLaravel = (
    <CodeBlock>
{kw('public function')}{' initiateBvn('}{kw('string')}{' $bvn): '}{kw('array')}{'\n'}
{'{\n'}
{'    '}{kw('return')}{' $this->client()\n'}
{'        ->post('}{str("'/api/v2/identity-verification/bvn/initiate'")}{', ['}{str("'bvn'")}{' => $bvn])\n'}
{'        ->throw()\n'}
{'        ->json();\n'}
{'}'}
    </CodeBlock>
);

const bvnInitiateCurl = (
    <CodeBlock>
{'curl -X POST '}{str('"$WAYA_BASE/api/v2/identity-verification/bvn/initiate"')} {'\\\n'}
{'  -H '}{str('"Authorization: Bearer $WAYA_SECRET_KEY"')} {'\\\n'}
{'  -H '}{str('"X-Merchant-Id: $WAYA_MERCHANT_ID"')} {'\\\n'}
{'  -H '}{str("'Content-Type: application/json'")} {'\\\n'}
{'  -d '}{str(`'{ "bvn": "22222222222" }'`)}
    </CodeBlock>
);

const bvnInitiateRequest = (
    <CodeBlock>
{'{ '}{str('"bvn"')}: {str('"22222222222"')}{' }'}   {com('// exactly 11 digits')}
    </CodeBlock>
);

const bvnInitiateResponse = (
    <CodeBlock>
{'{\n'}
{'  '}{str('"success"')}: {kw('true')},{'\n'}
{'  '}{str('"code"')}: {str('"00"')},{'\n'}
{'  '}{str('"message"')}: {str('"A verification code has been sent to the phone number registered against this BVN"')},{'\n'}
{'  '}{str('"data"')}: {'{\n'}
{'    '}{str('"reference"')}: {str('"9f1c4e2a-3b77-4d58-9a21-6c0f8e5b7d44"')},{'\n'}
{'    '}{str('"phoneNumber"')}: {str('"234****0000"')},{'\n'}
{'    '}{str('"expiresAt"')}: {str('"2026-10-13T14:32:10.482"')}{'\n'}
{'  }\n'}
{'}'}
    </CodeBlock>
);

/* ---------- Step 2: complete ---------- */

const bvnCompleteNode = (
    <CodeBlock>
{kw('async')} completeBvn(reference, otp) {'{\n'}
{'  '}{kw('const')} {'{ data } = '}{kw('await this')}.client.post({str("'/api/v2/identity-verification/bvn/complete'")}, {'{ reference, otp }'});{'\n'}
{'  '}{kw('return')} data;{'\n'}
{'}\n\n'}
{com('// usage')}{'\n'}
{kw('const')} id = {kw('await')} waya.completeBvn(reference, {str("'123456'")});{'\n'}
console.log(id.data.firstName, id.data.lastName); {com("// don't log or cache the full record")}
    </CodeBlock>
);

const bvnCompleteJava = (
    <CodeBlock>
{kw('public')}{' Mono<Map> completeBvn(String reference, String otp) {'}{'\n'}
{'    '}{kw('return')}{' client.post()\n'}
{'        .uri('}{str('"/api/v2/identity-verification/bvn/complete"')}{')\n'}
{'        .bodyValue(Map.of('}{str('"reference"')}{', reference, '}{str('"otp"')}{', otp))\n'}
{'        .retrieve()\n'}
{'        .bodyToMono(Map.class);\n'}
{'}'}
    </CodeBlock>
);

const bvnCompletePhp = (
    <CodeBlock>
{kw('public function')}{' completeBvn('}{kw('string')}{' $reference, '}{kw('string')}{' $otp): '}{kw('array')}{'\n'}
{'{\n'}
{'    $response = $this->client->post('}{str("'/api/v2/identity-verification/bvn/complete'")}{', [\n'}
{'        '}{str("'json'")}{' => ['}{str("'reference'")}{' => $reference, '}{str("'otp'")}{' => $otp]\n'}
{'    ]);\n'}
{'    '}{kw('return')}{' json_decode($response->getBody()->getContents(), '}{kw('true')}{');\n'}
{'}'}
    </CodeBlock>
);

const bvnCompleteCsharp = (
    <CodeBlock>
{kw('public async')}{' Task<JsonElement> CompleteBvnAsync('}{kw('string')}{' reference, '}{kw('string')}{' otp)\n'}
{'{\n'}
{'    '}{kw('var')}{' response = '}{kw('await')}{' _http.PostAsJsonAsync('}{str('"/api/v2/identity-verification/bvn/complete"')}{', '}{kw('new')}{' { reference, otp });\n'}
{'    response.EnsureSuccessStatusCode();\n'}
{'    '}{kw('return await')}{' response.Content.ReadFromJsonAsync<JsonElement>();\n'}
{'}'}
    </CodeBlock>
);

const bvnCompleteGo = (
    <CodeBlock>
{kw('func')}{' (c *Client) CompleteBvn(reference, otp '}{kw('string')}{') (map[string]interface{}, error) {'}{'\n'}
{'    '}{kw('var')}{' result map[string]interface{}\n'}
{'    _, err := c.http.R().\n'}
{'        SetBody(map[string]string{'}{str('"reference"')}{': reference, '}{str('"otp"')}{': otp}).\n'}
{'        SetResult(&result).\n'}
{'        Post('}{str('"/api/v2/identity-verification/bvn/complete"')}{')\n'}
{'    '}{kw('return')}{' result, err\n'}
{'}'}
    </CodeBlock>
);

const bvnCompleteLaravel = (
    <CodeBlock>
{kw('public function')}{' completeBvn('}{kw('string')}{' $reference, '}{kw('string')}{' $otp): '}{kw('array')}{'\n'}
{'{\n'}
{'    '}{kw('return')}{' $this->client()\n'}
{'        ->post('}{str("'/api/v2/identity-verification/bvn/complete'")}{', [\n'}
{'            '}{str("'reference'")}{' => $reference,\n'}
{'            '}{str("'otp'")}{'       => $otp,\n'}
{'        ])\n'}
{'        ->throw()\n'}
{'        ->json();\n'}
{'}'}
    </CodeBlock>
);

const bvnCompleteCurl = (
    <CodeBlock>
{'curl -X POST '}{str('"$WAYA_BASE/api/v2/identity-verification/bvn/complete"')} {'\\\n'}
{'  -H '}{str('"Authorization: Bearer $WAYA_SECRET_KEY"')} {'\\\n'}
{'  -H '}{str('"X-Merchant-Id: $WAYA_MERCHANT_ID"')} {'\\\n'}
{'  -H '}{str("'Content-Type: application/json'")} {'\\\n'}
{'  -d '}{str(`'{ "reference": "<reference from step 1>", "otp": "123456" }'`)}
    </CodeBlock>
);

const bvnCompleteRequest = (
    <CodeBlock>
{'{ '}{str('"reference"')}: {str('"9f1c4e2a-3b77-4d58-9a21-6c0f8e5b7d44"')}, {str('"otp"')}: {str('"123456"')}{' }'}
    </CodeBlock>
);

const bvnCompleteResponse = (
    <CodeBlock>
{'{\n'}
{'  '}{str('"success"')}: {kw('true')},{'\n'}
{'  '}{str('"code"')}: {str('"00"')},{'\n'}
{'  '}{str('"message"')}: {str('"BVN verification completed"')},{'\n'}
{'  '}{str('"data"')}: {'{\n'}
{'    '}{str('"bvn"')}: {str('"22222222222"')},{'\n'}
{'    '}{str('"firstName"')}: {str('"Test"')},{'\n'}
{'    '}{str('"lastName"')}: {str('"User"')},{'\n'}
{'    '}{str('"dateOfBirth"')}: {str('"01-Jan-1990"')},{'\n'}
{'    '}{str('"phoneNumber1"')}: {str('"2340000000000"')},{'\n'}
{'    '}{str('"gender"')}: {str('"Male"')},{'\n'}
{'    '}{str('"nationality"')}: {str('"Nigeria"')},{'\n'}
{'    '}{str('"watchListed"')}: {str('"SANDBOX"')}{'\n'}
{'  }\n'}
{'}'}
    </CodeBlock>
);

/* ============================================================
   COLLECTION - Payment collect  (POST /api/v2/payment-collect/initiate)
   ============================================================ */

const collectNode = (
    <CodeBlock>
{kw('async')} initiatePayment(payload) {'{\n'}
{'  '}{kw('const')} {'{ data } = '}{kw('await this')}.client.post({str("'/api/v2/payment-collect/initiate'")}, payload);{'\n'}
{'  '}{kw('return')} data;{'\n'}
{'}\n\n'}
{com('// usage')}{'\n'}
{kw('const')} res = {kw('await')} waya.initiatePayment({'{\n'}
{'  firstName: '}{str("'John'")}, lastName: {str("'Doe'")},{'\n'}
{'  description: '}{str("'Order #1234'")},{'\n'}
{'  transactionId: '}{str("'TXN-V2-20260604-01'")},{'\n'}
{'  currency: '}{str("'NGN'")}, email: {str("'john@example.com'")},{'\n'}
{'  amount: '}{str("'1500.00'")}, phone: {str("'08012345678'")},{'\n'}
{'  meta: { orderId: '}{str("'1234'")}{' }\n'}
{'});\n\n'}
console.log(res.data.checkOutUrl); {com('// redirect the customer here')}
    </CodeBlock>
);

const collectJava = (
    <CodeBlock>
{kw('public')}{' Mono<Map> initiatePayment(Map<String, Object> payload) {'}{'\n'}
{'    '}{kw('return')}{' client.post()\n'}
{'        .uri('}{str('"/api/v2/payment-collect/initiate"')}{')\n'}
{'        .bodyValue(payload)\n'}
{'        .retrieve()\n'}
{'        .bodyToMono(Map.class);\n'}
{'}'}
    </CodeBlock>
);

const collectPhp = (
    <CodeBlock>
{kw('public function')}{' initiatePayment('}{kw('array')}{' $payload): '}{kw('array')}{'\n'}
{'{\n'}
{'    $response = $this->client->post('}{str("'/api/v2/payment-collect/initiate'")}{', [\n'}
{'        '}{str("'json'")}{' => $payload\n'}
{'    ]);\n'}
{'    '}{kw('return')}{' json_decode($response->getBody()->getContents(), '}{kw('true')}{');\n'}
{'}'}
    </CodeBlock>
);

const collectCsharp = (
    <CodeBlock>
{kw('public async')}{' Task<JsonElement> InitiatePaymentAsync('}{kw('object')}{' payload)\n'}
{'{\n'}
{'    '}{kw('var')}{' response = '}{kw('await')}{' _http.PostAsJsonAsync('}{str('"/api/v2/payment-collect/initiate"')}{', payload);\n'}
{'    response.EnsureSuccessStatusCode();\n'}
{'    '}{kw('return await')}{' response.Content.ReadFromJsonAsync<JsonElement>();\n'}
{'}'}
    </CodeBlock>
);

const collectGo = (
    <CodeBlock>
{kw('func')}{' (c *Client) InitiatePayment(payload map[string]interface{}) (map[string]interface{}, error) {'}{'\n'}
{'    '}{kw('var')}{' result map[string]interface{}\n'}
{'    _, err := c.http.R().SetBody(payload).SetResult(&result).\n'}
{'        Post('}{str('"/api/v2/payment-collect/initiate"')}{')\n'}
{'    '}{kw('return')}{' result, err\n'}
{'}'}
    </CodeBlock>
);

const collectLaravel = (
    <CodeBlock>
{kw('public function')}{' initiatePayment('}{kw('array')}{' $payload): '}{kw('array')}{'\n'}
{'{\n'}
{'    '}{kw('return')}{' $this->client()\n'}
{'        ->post('}{str("'/api/v2/payment-collect/initiate'")}{', $payload)\n'}
{'        ->throw()\n'}
{'        ->json();\n'}
{'}'}
    </CodeBlock>
);

const collectCurl = (
    <CodeBlock>
{'curl -X POST \\\n'}
{'  '}{str("'$WAYA_BASE/api/v2/payment-collect/initiate'")} {'\\\n'}
{'  -H '}{str("'Authorization: Bearer $WAYA_SECRET_KEY'")} {'\\\n'}
{'  -H '}{str("'X-Merchant-Id: $WAYA_MERCHANT_ID'")} {'\\\n'}
{'  -H '}{str("'Content-Type: application/json'")} {'\\\n'}
{'  -d '}{str(`'{
        "firstName": "John", "lastName": "Doe",
        "transactionId": "TXN-V2-20260604-01",
        "currency": "NGN", "email": "john@example.com",
        "amount": "1500.00", "phone": "08012345678",
        "meta": { "orderId": "1234" }
      }'`)}
    </CodeBlock>
);

const collectRequest = (
    <CodeBlock>
{'{\n'}
{'  '}{str('"firstName"')}: {str('"John"')},{'\n'}
{'  '}{str('"lastName"')}: {str('"Doe"')},{'\n'}
{'  '}{str('"description"')}: {str('"Order #1234"')},{'\n'}
{'  '}{str('"transactionId"')}: {str('"TXN-V2-20260604-01"')},{'\n'}
{'  '}{str('"currency"')}: {str('"NGN"')},{'\n'}
{'  '}{str('"email"')}: {str('"john@example.com"')},{'\n'}
{'  '}{str('"amount"')}: {str('"1500.00"')},{'\n'}
{'  '}{str('"phone"')}: {str('"08012345678"')},{'\n'}
{'  '}{str('"meta"')}: {'{ '}{str('"orderId"')}: {str('"1234"')}{' }\n'}
{'}'}
    </CodeBlock>
);

const collectResponse = (
    <CodeBlock>
{'{\n'}
{'  '}{str('"success"')}: {kw('true')},{'\n'}
{'  '}{str('"code"')}: {str('"00"')},{'\n'}
{'  '}{str('"message"')}: {str('"Payment initiated successfully"')},{'\n'}
{'  '}{str('"data"')}: {'{\n'}
{'    '}{str('"uniqueId"')}: {str('"TXN-V2-20260604-01"')},{'\n'}
{'    '}{str('"transactionId"')}: {str('"178056425312256824"')},{'\n'}
{'    '}{str('"email"')}: {str('"john@example.com"')},{'\n'}
{'    '}{str('"amount"')}: {str('"1500.00"')},{'\n'}
{'    '}{str('"checkOutUrl"')}: {str('"https://pay.wayaquick.com/?_tranId=178056425312256824"')},{'\n'}
{'    '}{str('"merchantId"')}: {str('"MER_LOfV71779980928158w7MEX"')},{'\n'}
{'    '}{str('"expiresAt"')}: {str('"2026-06-04T09:40:00Z"')}{'\n'}
{'  }\n'}
{'}'}
    </CodeBlock>
);

const collectStatusResponse = (
    <CodeBlock>
{'{\n'}
{'  '}{str('"success"')}: {kw('true')},{'\n'}
{'  '}{str('"code"')}: {str('"00"')},{'\n'}
{'  '}{str('"message"')}: {str('"Transaction status retrieved"')},{'\n'}
{'  '}{str('"data"')}: {'{\n'}
{'    '}{str('"refNo"')}: {str('"1779662251460508970"')},{'\n'}
{'    '}{str('"tranId"')}: {str('"aff8a062-4aa8-413b-9d2d-834b2e1dff82"')},{'\n'}
{'    '}{str('"merchantId"')}: {str('"MER_BdVFq…"')},{'\n'}
{'    '}{str('"amount"')}: {str('"1500.00"')},{'\n'}
{'    '}{str('"customerEmail"')}: {str('"john@example.com"')},{'\n'}
{'    '}{str('"amountPaid"')}: {str('"1500.00"')},{'\n'}
{'    '}{str('"fee"')}: {str('"22.50"')},{'\n'}
{'    '}{str('"currencyCode"')}: {str('"NGN"')},{'\n'}
{'    '}{str('"status"')}: {str('"SUCCESSFUL"')},{'\n'}
{'    '}{str('"settlementStatus"')}: {str('"PENDING"')},{'\n'}
{'    '}{str('"channel"')}: {str('"CARD"')},{'\n'}
{'    '}{str('"processedBy"')}: {str('"ISW"')},{'\n'}
{'    '}{str('"description"')}: {str('"Order #4523"')},{'\n'}
{'    '}{str('"environment"')}: {str('"LIVE"')},{'\n'}
{'    '}{str('"tranDate"')}: {str('"2026-06-08T10:37:40.348493"')},{'\n'}
{'    '}{str('"expiresAt"')}: {str('"2026-06-08T09:47:40Z"')}{'\n'}
{'  }\n'}
{'}'}
    </CodeBlock>
);

const collectStatusCurl = (
    <CodeBlock>
{'curl -X GET \\\n'}
{'  '}{str("'$WAYA_BASE/api/v2/payment-collect/status/1779662251460508970'")} {'\\\n'}
{'  -H '}{str("'Authorization: Bearer $WAYA_SECRET_KEY'")} {'\\\n'}
{'  -H '}{str("'X-Merchant-Id: $WAYA_MERCHANT_ID'")}
    </CodeBlock>
);

/* ============================================================
   TRANSACTION WEBHOOK
   ============================================================ */

const webhookRequestExample = (
    <CodeBlock>
{com('# WayaQuick -> your server, on SUCCESSFUL / FAILED')}{'\n'}
{kw('POST')}{' <your webhook URL>\n'}
{'Content-Type:                application/json\n'}
{'X-Waya-Timestamp:            1780980000123\n'}
{'X-Waya-Signature:            5sLnE7s+r0qN…=\n'}
{'X-Waya-Signature-Algorithm:  HmacSHA256'}
    </CodeBlock>
);

const webhookBody = (
    <CodeBlock>
{'{\n'}
{'  '}{str('"OrderId"')}: {str('"1779662251460508970"')},{'\n'}
{'  '}{str('"Amount"')}: {num('1500.00')},{'\n'}
{'  '}{str('"Description"')}: {str('"Order #4523"')},{'\n'}
{'  '}{str('"Fee"')}: {num('22.50')},{'\n'}
{'  '}{str('"Currency"')}: {str('"NGN"')},{'\n'}
{'  '}{str('"Status"')}: {str('"SUCCESSFUL"')},{'\n'}
{'  '}{str('"TranTime"')}: {str('"2026-06-07T14:30:12"')},{'\n'}
{'  '}{str('"TransactionDate"')}: {str('"2026-06-07 14:30:12"')},{'\n'}
{'  '}{str('"productName"')}: {str('"CARD"')},{'\n'}
{'  '}{str('"businessName"')}: {str('"Your Shop Ltd"')},{'\n'}
{'  '}{str('"customer"')}: {'{\n'}
{'    '}{str('"name"')}: {str('"John Doe"')},{'\n'}
{'    '}{str('"email"')}: {str('"john@example.com"')},{'\n'}
{'    '}{str('"phoneNumber"')}: {str('"08012345678"')},{'\n'}
{'    '}{str('"customerId"')}: {str('"CUS_…"')}{'\n'}
{'  },\n'}
{'  '}{str('"merchantId"')}: {str('"MER_…"')},{'\n'}
{'  '}{str('"branchCategory"')}: {kw('null')},{'\n'}
{'  '}{str('"recurrentPayment"')}: {kw('false')}{'\n'}
{'}'}
    </CodeBlock>
);

const webhookSignatureFormula = (
    <CodeBlock>
{'Base64( HMAC-SHA256( "<X-Waya-Timestamp>" + "." + "<raw request body>", merchant_secret ) )'}
    </CodeBlock>
);

const webhookNode = (
    <CodeBlock>
{kw('const')} crypto = {kw('require')}({str("'crypto'")});{'\n\n'}
{kw('function')}{' verifyWayaSignature(rawBody, headers, secret) {'}{'\n'}
{'  '}{kw('const')} ts  = headers[{str("'x-waya-timestamp'")}];{'\n'}
{'  '}{kw('const')} sig = headers[{str("'x-waya-signature'")}];{'\n'}
{'  '}{kw('if')}{' (!ts || !sig) '}{kw('return')}{' '}{kw('false')}{';\n\n'}
{'  '}{kw('const')} signedPayload = {str('`')}{`\${ts}.\${rawBody}`}{str('`')};{'\n'}
{'  '}{kw('const')}{' expected = crypto\n'}
{'    .createHmac('}{str("'sha256'")}{', secret)\n'}
{'    .update(signedPayload)\n'}
{'    .digest('}{str("'base64'")}{');\n\n'}
{'  '}{com('// constant-time compare')}{'\n'}
{'  '}{kw('return')}{' crypto.timingSafeEqual(Buffer.from(expected), Buffer.from(sig));\n'}
{'}'}
    </CodeBlock>
);

const webhookPython = (
    <CodeBlock>
{kw('import')}{' hmac, hashlib, base64\n\n'}
{kw('def')}{' verify_waya_signature(raw_body: bytes, headers, secret: str) -> bool:\n'}
{'    ts  = headers.get('}{str('"X-Waya-Timestamp"')}{')\n'}
{'    sig = headers.get('}{str('"X-Waya-Signature"')}{')\n'}
{'    '}{kw('if')}{' not ts or not sig:\n'}
{'        '}{kw('return')}{' '}{kw('False')}{'\n'}
{'    signed_payload = f'}{str('"{ts}.{raw_body.decode(\'utf-8\')}"')}{'.encode()\n'}
{'    expected = base64.b64encode(\n'}
{'        hmac.new(secret.encode(), signed_payload, hashlib.sha256).digest()\n'}
{'    ).decode()\n'}
{'    '}{kw('return')}{' hmac.compare_digest(expected, sig)'}
    </CodeBlock>
);

const webhookPhp = (
    <CodeBlock>
{kw('function')}{' verifyWayaSignature(string $rawBody, array $headers, string $secret): bool {'}{'\n'}
{'    $ts  = $headers['}{str("'X-Waya-Timestamp'")}{'] ?? null;\n'}
{'    $sig = $headers['}{str("'X-Waya-Signature'")}{']  ?? null;\n'}
{'    '}{kw('if')}{' (!$ts || !$sig) '}{kw('return')}{' '}{kw('false')}{';\n\n'}
{'    $expected = base64_encode(\n'}
{'        hash_hmac('}{str("'sha256'")}{', $ts . '}{str("'.'")}{' . $rawBody, $secret, '}{kw('true')}{')\n'}
{'    );\n'}
{'    '}{kw('return')}{' hash_equals($expected, $sig);\n'}
{'}\n\n'}
{com('// Grab the raw body before any JSON parsing')}{'\n'}
{'$raw = file_get_contents('}{str("'php://input'")}{');'}
    </CodeBlock>
);

const webhookJava = (
    <CodeBlock>
{kw('import')}{' javax.crypto.Mac;\n'}
{kw('import')}{' javax.crypto.spec.SecretKeySpec;\n'}
{kw('import')}{' java.nio.charset.StandardCharsets;\n'}
{kw('import')}{' java.security.MessageDigest;\n'}
{kw('import')}{' java.util.Base64;\n\n'}
{kw('public static')}{' boolean verifyWayaSignature(String rawBody, String ts, String sig, String secret) throws Exception {'}{'\n'}
{'    '}{kw('if')}{' (ts == null || sig == null) '}{kw('return')}{' '}{kw('false')}{';\n'}
{'    Mac mac = Mac.getInstance('}{str('"HmacSHA256"')}{');\n'}
{'    mac.init('}{kw('new')}{' SecretKeySpec(secret.getBytes(StandardCharsets.UTF_8), '}{str('"HmacSHA256"')}{'));\n'}
{'    String expected = Base64.getEncoder().encodeToString(\n'}
{'            mac.doFinal((ts + '}{str('"."')}{' + rawBody).getBytes(StandardCharsets.UTF_8)));\n'}
{'    '}{kw('return')}{' MessageDigest.isEqual(\n'}
{'            expected.getBytes(StandardCharsets.UTF_8),\n'}
{'            sig.getBytes(StandardCharsets.UTF_8));\n'}
{'}'}
    </CodeBlock>
);

const webhookReplay = (
    <CodeBlock>
{'now_ms = '}{kw('int')}{'(time.time() * '}{num('1000')}{')\n'}
{kw('if')}{' abs(now_ms - '}{kw('int')}{'(ts)) > '}{num('5')}{' * '}{num('60')}{' * '}{num('1000')}{':\n'}
{'    '}{kw('return')}{' reject('}{str('"stale or future-dated webhook"')}{')'}
    </CodeBlock>
);

const webhookE2E = (
    <CodeBlock>
{kw('POST')}{' /waya/webhook HTTP/1.1\n'}
{'Host: api.your-shop.com\n'}
{'Content-Type: application/json\n'}
{'X-Waya-Timestamp: 1780980012345\n'}
{'X-Waya-Signature: q1H7M3+UvIa6jL1ZbZ+ezTLm4eOpZP1pVgF1V0n+8AA=\n'}
{'X-Waya-Signature-Algorithm: HmacSHA256\n\n'}
{str('{"OrderId":"1779662251460508970","Amount":1500.00,"Description":"Order #4523","Fee":15.00,"Currency":"NGN","Status":"SUCCESSFUL","TranTime":"2026-06-07T14:30:12","TransactionDate":"2026-06-07 14:30:12","productName":"CARD","businessName":"Your Shop Ltd","customer":{"name":"John Doe","email":"john@example.com","phoneNumber":"08012345678","customerId":"CUS_abc"},"merchantId":"MER_xyz","recurrentPayment":false}')}
    </CodeBlock>
);

/* ============================================================
   Main page component
   ============================================================ */

function DirectWebIntegration() {
    const contents = [
        'Introduction',
        'Authentication',
        'Response Envelope',
        'Payout',
        'Collection',
        'BVN Verification',
        'Webhook',
    ];

    const setupTabs = langTabs('setup', {
        curl: setupCurl, node: setupNode, java: setupJava, php: setupPhp, csharp: setupCsharp,
        go: setupGo, laravel: setupLaravel,
    });
    const banksTabs = langTabs('banks', {
        curl: banksCurl, node: banksNode, java: banksJava, php: banksPhp, csharp: banksCsharp,
        go: banksGo, laravel: banksLaravel,
    });
    const verifyAcctTabs = langTabs('va', {
        curl: verifyAcctCurl, node: verifyAcctNode, java: verifyAcctJava, php: verifyAcctPhp, csharp: verifyAcctCsharp,
        go: verifyAcctGo, laravel: verifyAcctLaravel,
    });
    const payoutTabs = langTabs('payout', {
        curl: payoutCurl, node: payoutNode, java: payoutJava, php: payoutPhp, csharp: payoutCsharp,
        go: payoutGo, laravel: payoutLaravel,
    });
    const bvnInitiateTabs = langTabs('bvn-init', {
        curl: bvnInitiateCurl, node: bvnInitiateNode, java: bvnInitiateJava, php: bvnInitiatePhp, csharp: bvnInitiateCsharp,
        go: bvnInitiateGo, laravel: bvnInitiateLaravel,
    });
    const bvnCompleteTabs = langTabs('bvn-complete', {
        curl: bvnCompleteCurl, node: bvnCompleteNode, java: bvnCompleteJava, php: bvnCompletePhp, csharp: bvnCompleteCsharp,
        go: bvnCompleteGo, laravel: bvnCompleteLaravel,
    });
    const collectTabs = langTabs('collect', {
        curl: collectCurl, node: collectNode, java: collectJava, php: collectPhp, csharp: collectCsharp,
        go: collectGo, laravel: collectLaravel,
    });

    // Webhook verification ships in the four languages the spec provides.
    const webhookVerifyTabs = [
        { id: 'wh-node', label: 'Node.js', content: webhookNode },
        { id: 'wh-python', label: 'Python', content: webhookPython },
        { id: 'wh-php', label: 'PHP', content: webhookPhp },
        { id: 'wh-java', label: 'Java', content: webhookJava },
    ];

    return (
        <div className="wq-root">
          <Seo
                title="Standard API Integration"
                description="WayaQuick Merchant API reference: authentication, payouts, account verification, BVN verification, payment collection and webhooks."
            />
            <Navbar />
            <div className="w-full flex">
                <Sidebar />
                <div className="documentation-container px-8 py-4 flex items-start">
                    <div className="content">

                        {/* HERO */}
                        <section className="wq-hero">
                            <div className="wq-hero-eyebrow">DEVELOPERS · Merchant API</div>
                            <h1 className="wq-h1">
                                WayaQuick — <em>Standard API Integration</em>
                            </h1>
                        </section>

                        {/* INTRODUCTION */}
                        <section className="wq-section" id={contents[0]}>
                            <h6 className="wq-h2">Introduction</h6>
                            <p className="wq-lead">
                                The WayaQuick Merchant API lets you collect customer payments and send payouts (plus verify accounts and identity) in Nigeria. Authentication uses API-key headers, every endpoint returns the same response envelope, and your unique reference on each operation is its idempotency and reconciliation key.
                            </p>

                            <p className="wq-p">This guide is organised into these sections:</p>
                            <ul className="wq-p" style={{ listStyle: 'disc', paddingLeft: '1.25rem' }}>
                                <li><strong>Payout</strong> — Verify Account &amp; Initiate Payout (banks, name enquiry, disbursement).</li>
                                <li><strong>Collection</strong> — Payment Collect (hosted checkout).</li>
                                <li><strong>BVN Verification</strong> — KYC identity lookup.</li>
                                <li><strong>Webhook</strong> — Receive and verify transaction notifications.</li>
                            </ul>

                            <h4 className="wq-h4">Base URL</h4>
                            <div className="wq-endpoint break-all text-wrap">
                                <span className="wq-endpoint-path">{BASE_URL}</span>
                            </div>
                            <p className="wq-p">
                            Whether your account operates in test or live mode is controlled by the Test / Live toggle on your merchant dashboard.
                            </p>
                        </section>

                        {/* AUTHENTICATION */}
                        <section className="wq-section" id={contents[1]}>
                            <h2 className="wq-h2">Authentication</h2>
                            <p className="wq-p">Authentication uses API-key headers on every request:</p>

                            <table className="wq-table">
                                <thead><tr><th>Header</th><th>Value</th></tr></thead>
                                <tbody>
                                    <tr><td><code>Authorization</code></td><td><code>Bearer {'{your_api_secret_key}'}</code> (<code>WAYASECK_TEST_0x...</code> or <code>WAYASECK_PROD_0x...</code>)</td></tr>
                                    <tr><td><code>X-Merchant-Id</code></td><td>Your Merchant ID (format <code>MER_...</code>)</td></tr>
                                    <tr><td><code>Content-Type</code></td><td><code>application/json</code></td></tr>
                                </tbody>
                            </table>

                            <Note label="Keep it secret">
                                Generate your secret key on the dashboard. The <code>_TEST_</code> / <code>_PROD_</code> prefix selects the environment automatically. A missing or invalid key returns 401. Never expose your secret key in a browser or mobile app — these calls are server-to-server.
                            </Note>

                            <h4 className="wq-h4">Client setup</h4>
                            <p className="wq-p">
                                A reusable client per language that carries the auth headers on every request. Point <code>baseUrl</code> at production when you go live.
                            </p>
                            <Tabs tabs={setupTabs} />
                        </section>

                        {/* RESPONSE ENVELOPE */}
                        <section className="wq-section" id={contents[2]}>
                            <h2 className="wq-h2">Response Envelope</h2>
                            <p className="wq-p">Every endpoint returns the same envelope:</p>
                            {responseEnvelope}
                            <p className="wq-p">
                                <code>code</code> "00" (and <code>success: true</code>) means success. <code>data</code> is the endpoint payload (null on errors). The code is a bank-style response code, derived from the HTTP status:
                            </p>
                            <table className="wq-table">
                                <thead><tr><th>Code</th><th>Meaning</th><th>HTTP Status</th></tr></thead>
                                <tbody>
                                    <tr><td><code>00</code></td><td>Success</td><td className="wq-type">200 / 201 / 202</td></tr>
                                    <tr><td><code>01</code></td><td>Unauthorized — missing/invalid API key</td><td className="wq-type">401</td></tr>
                                    <tr><td><code>13</code></td><td>Validation error — bad/missing fields</td><td className="wq-type">400, 422</td></tr>
                                    <tr><td><code>25</code></td><td>Not found — account / record not resolved</td><td className="wq-type">404</td></tr>
                                    <tr><td><code>57</code></td><td>Forbidden</td><td className="wq-type">403</td></tr>
                                    <tr><td><code>91</code></td><td>Upstream/provider unavailable</td><td className="wq-type">502, 503, 504</td></tr>
                                    <tr><td><code>92</code></td><td>System busy — try again shortly</td><td className="wq-type">429</td></tr>
                                    <tr><td><code>94</code></td><td>Duplicate — reference already used</td><td className="wq-type">409</td></tr>
                                    <tr><td><code>96</code></td><td>Unexpected system error</td><td className="wq-type">500 (and other 5xx)</td></tr>
                                </tbody>
                            </table>
                            <p className="wq-p">On any error, <code>success</code> is false, <code>data</code> is null, and <code>message</code> describes the problem:</p>
                            {responseErrorEnvelope}
                        </section>

                        <Flourish />

                        {/* PAYOUT */}
                        <section className="wq-section" id={contents[3]}>
                            <h2 className="wq-h2">Payout</h2>
                            <p className="wq-lead">
                                Verify Account &amp; Initiate Payout. Discover banks, resolve the destination account name (and optionally verify identity), then transfer funds. Always verify the destination account before initiating a payout.
                            </p>

                            {/* Get bank list */}
                            <h3 className="wq-h3">Get bank list</h3>
                            <p className="wq-p">
                                Returns all supported banks and their codes. Use these codes for payouts and account verification.
                            </p>
                            <Endpoint method="GET" path="/api/v2/get-bank-list" />
                            <h4 className="wq-h4">Example</h4>
                            <Tabs tabs={banksTabs} />
                            <h4 className="wq-h4">Response</h4>
                            {banksResponse}

                            {/* Account verification */}
                            <h3 className="wq-h3">Account verification (name enquiry)</h3>
                            <p className="wq-p">
                                Resolves an account number and returns the registered account name. Always call this before initiating a payout.
                            </p>
                            <Endpoint method="POST" path="/api/v2/verify-account" />
                            <h4 className="wq-h4">Request body</h4>
                            <table className="wq-table">
                                <thead><tr><th>Field</th><th>Type</th><th>Notes</th></tr></thead>
                                <tbody>
                                    <tr><td><code>accountNumber</code><Required /></td><td className="wq-type">string</td><td>10-digit NUBAN</td></tr>
                                    <tr><td><code>enquiryType</code><Required /></td><td className="wq-type">string</td><td><code>WAYA-BANK</code> (intra) or <code>OTHERS</code> (inter-bank)</td></tr>
                                    <tr><td><code>bankCode</code><Conditional /></td><td className="wq-type">string</td><td>CBN bank code from Get bank list. Required when <code>enquiryType</code> is OTHERS</td></tr>
                                </tbody>
                            </table>
                            {verifyAcctRequest}
                            <h4 className="wq-h4">Example</h4>
                            <Tabs tabs={verifyAcctTabs} />
                            <h4 className="wq-h4">Response</h4>
                            {verifyAcctResponse}

                            {/* Initiate payout */}
                            <h3 className="wq-h3">Initiate payout</h3>
                            <p className="wq-p">Transfer funds to a bank account.</p>
                            <Endpoint method="POST" path="/api/v2/payment-payout/initiate" />
                            <h4 className="wq-h4">Request body</h4>
                            <table className="wq-table">
                                <thead><tr><th>Field</th><th>Type</th><th>Notes</th></tr></thead>
                                <tbody>
                                    <tr><td><code>amount</code><Required /></td><td className="wq-type">number</td><td>Greater than 0</td></tr>
                                    <tr><td><code>currency</code><Required /></td><td className="wq-type">string</td><td>ISO-4217, e.g. NGN</td></tr>
                                    <tr><td><code>accountNumber</code><Required /></td><td className="wq-type">string</td><td>10-digit NUBAN</td></tr>
                                    <tr><td><code>bankCode</code><Required /></td><td className="wq-type">string</td><td>CBN bank code</td></tr>
                                    <tr><td><code>reference</code><Required /></td><td className="wq-type">string</td><td>Your unique reference / idempotency key</td></tr>
                                    <tr><td><code>accountName</code></td><td className="wq-type">string</td><td>Destination account name (match the verified name)</td></tr>
                                    <tr><td><code>narration</code></td><td className="wq-type">string</td><td>Accepted, but not yet applied: the beneficiary statement shows a system narration that includes your <code>reference</code></td></tr>
                                </tbody>
                            </table>
                            {payoutRequest}
                            <h4 className="wq-h4">Example</h4>
                            <Tabs tabs={payoutTabs} />
                            <h4 className="wq-h4">Response</h4>
                            {payoutResponse}

                            <Note label="Heads up">
                                A PROCESSING status means accepted, not settled. Confirm the result on your server (via your webhook or status check) with <code>payoutReference</code> before treating the money as delivered.
                            </Note>

                            <table className="wq-table">
                                <thead><tr><th>HTTP / code</th><th>When</th><th>Money moved?</th><th>What to do</th></tr></thead>
                                <tbody>
                                    <tr><td><code>201</code> / <code>00</code></td><td>Payout accepted</td><td>Yes, in progress</td><td>Track it with the status endpoint</td></tr>
                                    <tr><td><code>202</code> / <code>00</code> (<code>data.status</code> = <code>PENDING</code>)</td><td>The bank's outcome isn't confirmed yet</td><td>Unknown</td><td><strong>Don't resend.</strong> Query the status</td></tr>
                                    <tr><td><code>401</code> / <code>01</code></td><td>Missing/invalid API key</td><td>No</td><td>Fix the credentials</td></tr>
                                    <tr><td><code>403</code> / <code>57</code></td><td>Merchant account inactive</td><td>No</td><td>Contact support</td></tr>
                                    <tr><td><code>409</code> / <code>94</code></td><td>Insufficient wallet balance</td><td>No</td><td>Fund the wallet, then send it again</td></tr>
                                    <tr><td><code>422</code> / <code>13</code>, <code>message</code> starting with a field name</td><td>Request validation failed</td><td>No</td><td>Fix the request</td></tr>
                                    <tr><td><code>422</code> / <code>13</code>, any other <code>message</code></td><td>Payout rejected</td><td>Query the status to be sure</td><td>Query the status</td></tr>
                                    <tr><td><code>500</code> / <code>502</code> / <code>503</code> / <code>504</code>, timeout, dropped connection</td><td>Outcome unknown</td><td>Unknown</td><td><strong>Don't resend.</strong> Query the status</td></tr>
                                </tbody>
                            </table>

                            <Note tone="critical" label="Never resend">
                                Never resend a payout until its status is final. If the result is unknown, query <code>GET /api/v2/payment-payout/status/{'{reference}'}</code> with your <code>reference</code>. A payout can return 404 for a short time right after initiation; wait at least 2 minutes before treating a 404 as "not accepted".
                            </Note>

                            {/* Check payout status*/}
                            <h3 className="wq-h3">Check payout status</h3>
                            <p className="wq-p">Returns the latest status of a payout by the reference you sent at initiation.</p>
                            <Endpoint method="GET" path="/api/v2/payment-payout/status/{reference}" />
                            <Note label="Status heads up">
                                The status is scoped to the authenticated merchant (resolved from your API key). A reference that doesn't belong to this merchant — or in a different environment — returns 404
                            </Note>
                            <p className="wq-p">data.status is the transaction's lifecycle status:</p>
                            <h4 className="wq-h4">Request body</h4>
                            <table className="wq-table">
                                <thead><tr><th>data.status</th><th>Meaning</th></tr></thead>
                                <tbody>
                                    <tr><td><code>PENDING</code></td><td>Submitted; terminal outcome not yet recorded (reconciling).</td></tr>
                                    <tr><td><code>SUCCESS</code></td><td>Completed successfully.</td></tr>
                                    <tr><td><code>REVERSED</code></td><td>Failed/reversed — the merchant wallet was re-credited.</td></tr>
                                </tbody>
                            </table>
                            <h4 className="wq-h4">Example Response</h4>
                            {payoutStatusResponse}
                            <p className="wq-p">The top-level success is the API envelope flag; data.status is the actual payout status — don't confuse the two.</p>
                            {payoutStatusCurl}
                            <table className="wq-table">
                                <thead><tr><th>HTTP</th><th>WHEN</th></tr></thead>
                                <tbody>
                                    <tr><td><code>200</code></td><td>Status returned</td></tr>
                                    <tr><td><code>401</code></td><td>Missing/invalid API key.</td></tr>
                                    <tr><td><code>404</code></td><td>No payout with that reference for this merchant</td></tr>
                                    <tr><td><code>502</code></td><td>Status temporarily unavailable — try again shortly</td></tr>
                                </tbody>
                            </table>
                            <p className="wq-p">
                                <strong>Polling:</strong> query every 30 seconds for the first 10 minutes, then every 5 minutes, until the status is <code>SUCCESS</code> or <code>REVERSED</code>. Stop after 24 hours and contact support with the reference for any payout still <code>PENDING</code>.
                            </p>
                        </section>

                        <Flourish />

                        {/* COLLECTION */}
                        <section className="wq-section" id={contents[4]}>
                            <h2 className="wq-h2">Collection</h2>
                            <p className="wq-lead">
                                Payment Collect. Start collecting real-life payments from your customers. This starts a payment collection and returns a checkout URL you redirect your customer to.
                            </p>

                            <h3 className="wq-h3">Payment collect (initiate)</h3>
                            <p className="wq-p">
                                Starts a payment collection and returns a transaction ID and checkout URL you redirect your customer to.
                            </p>
                            <Endpoint method="POST" path="/api/v2/payment-collect/initiate" />
                            <h4 className="wq-h4">Request body</h4>
                            <table className="wq-table">
                                <thead><tr><th>Field</th><th>Type</th><th>Notes</th></tr></thead>
                                <tbody>
                                    <tr><td><code>transactionId</code><Required /></td><td className="wq-type">string</td><td>Your unique reference. Never reused: a repeat returns 409</td></tr>
                                    <tr><td><code>amount</code><Required /></td><td className="wq-type">string</td><td>Quoted string, at least "1.00", max 2 decimal places (e.g. "1500.00")</td></tr>
                                    <tr><td><code>currency</code><Required /></td><td className="wq-type">string</td><td>e.g. NGN</td></tr>
                                    <tr><td><code>firstName</code><Required /></td><td className="wq-type">string</td><td>Customer first name, 2–100 characters</td></tr>
                                    <tr><td><code>lastName</code><Required /></td><td className="wq-type">string</td><td>Customer last name, 2–100 characters</td></tr>
                                    <tr><td><code>email</code><Required /></td><td className="wq-type">string</td><td>Customer email (not format-checked)</td></tr>
                                    <tr><td><code>phone</code><Required /></td><td className="wq-type">string</td><td>Customer phone (not format-checked)</td></tr>
                                    <tr><td><code>description</code></td><td className="wq-type">string</td><td>Shown under the payment fields on the checkout page; cut to 100 characters</td></tr>
                                    <tr><td><code>meta</code></td><td className="wq-type">object</td><td>Arbitrary metadata; send a normal JSON object</td></tr>
                                </tbody>
                            </table>
                            <Note label="">
                                <code>merchantId</code> is taken from <code>X-Merchant-Id</code> automatically — do not send it in the body.
                            </Note>
                            {collectRequest}
                            <h4 className="wq-h4">Example</h4>
                            <Tabs tabs={collectTabs} />
                            <h4 className="wq-h4">Response</h4>
                            <p className="wq-p">Redirect the customer to <code>checkOutUrl</code> to complete payment:</p>
                            {collectResponse}

                            <Note label="Heads up">
                                When the customer finishes on the checkout page, confirm the result on your server before fulfilling the order.
                            </Note>

                            <table className="wq-table">
                                <thead><tr><th>HTTP / code</th><th>When</th><th>What to do</th></tr></thead>
                                <tbody>
                                    <tr><td><code>201</code> / <code>00</code></td><td>Collection created</td><td>Redirect the customer to <code>checkOutUrl</code></td></tr>
                                    <tr><td><code>409</code> / <code>94</code></td><td><code>transactionId</code> already used by your merchant account</td><td>Start a new collection with a new <code>transactionId</code></td></tr>
                                    <tr><td><code>422</code> / <code>13</code></td><td>A required field is missing or invalid; <code>message</code> names the field</td><td>Fix the request</td></tr>
                                    <tr><td><code>502</code> / <code>91</code></td><td>Payment gateway unavailable, or no checkout URL returned</td><td>Start a new collection with a new <code>transactionId</code></td></tr>
                                </tbody>
                            </table>

                            <p className="wq-p">
                                A <code>transactionId</code> is checked per merchant account and environment, and is never released. If initiation times out or fails, use a <strong>new</strong> <code>transactionId</code>: the customer never saw the lost checkout URL, so it can't be paid.
                            </p>

                            <Note label="Checkout links expire">
                                <code>expiresAt</code> (in the initiate and status responses) is the moment the link stops accepting payment, in UTC (ISO-8601 with <code>Z</code>). After it, new payment attempts on the link are rejected. Before sending a customer back to an existing <code>checkOutUrl</code>, check its status: only reopen it while the status is <code>PENDING</code> and <code>expiresAt</code> hasn't passed.
                            </Note>

                            <p className="wq-p">
                                <strong>Bank transfers must be for the exact amount.</strong> A transfer for a different amount fails and is returned to the sender.
                            </p>

                            <h4 className="wq-h4">Confirming a collection</h4>
                            <p className="wq-p">Use the deposit webhook and the status query together:</p>
                            <ol className="wq-p" style={{ listStyle: 'decimal', paddingLeft: '1.25rem' }}>
                                <li><strong>On receiving a webhook</strong>, don't credit from the webhook alone. Call <code>GET /api/v2/payment-collect/status/{'{refNo}'}</code> with the webhook's <code>OrderId</code>, and credit only when the status is <code>SUCCESSFUL</code>, using the <code>amountPaid</code> shown there.</li>
                                <li><strong>If no webhook arrives</strong>, poll the status endpoint every 30 seconds until the collection reaches a final status.</li>
                                <li><strong>Credit each <code>refNo</code> once</strong>, even if both the webhook and polling confirm it.</li>
                            </ol>

                            {/* Collection status */}
                            <h3 className="wq-h3">Check Collection (deposit) status</h3>
                            <p className="wq-p">
                                Returns the current state of a deposit by its refNo. Use this for reconciliation alongside the deposit webhook + email — the webhook is the primary signal; this endpoint is the pull/safety-net path.
                            </p>
                            <Endpoint method="GET" path="/api/v2/payment-collect/status/{refNo}" />
                            <Note label="Heads up">
                                refNo is the transaction reference (the gateway transactionId / the webhook OrderId). The lookup is scoped to the authenticated merchant — a reference that belongs to another merchant returns 404.
                            </Note>
                            <h4 className="wq-h4">Response</h4>
                            {collectStatusResponse}
                            <p className="wq-p">
                                <code>amount</code> is the expected amount; <code>amountPaid</code> is what was actually received. Use <code>status</code> + <code>amountPaid</code> as authoritative.
                            </p>
                            <h4 className="wq-h4">data.status values</h4>
                            <table className="wq-table">
                                <thead><tr><th>Status</th><th>Terminal</th><th>Meaning</th></tr></thead>
                                <tbody>
                                    <tr><td><code>INITIATED / PENDING / PROCESSING / APPROVED</code></td><td className="wq-type">no</td><td>In flight — keep polling; don't refund or retry</td></tr>
                                    <tr><td><code>SUCCESSFUL</code></td><td className="wq-type">yes</td><td>Funds confirmed — fulfil (use refNo for idempotency)</td></tr>
                                    <tr><td><code>REFUNDED</code></td><td className="wq-type">yes</td><td>Previously-successful transaction refunded</td></tr>
                                    <tr><td><code>FAILED / DECLINED / REJECTED / ABANDONED / EXPIRED / CANCELLED / CUSTOMER_ERROR / FRAUD_ERROR</code></td><td className="wq-type">yes</td><td>Customer not debited — no fulfilment</td></tr>
                                    <tr><td><code>TIMEOUT / ERROR / SYSTEM_ERROR / BANK_ERROR</code></td><td className="wq-type">yes</td><td>Outcome unknown — reconcile, don't refund unilaterally</td></tr>
                                </tbody>
                            </table>

                            {collectStatusCurl}

                            <table className="wq-table">
                                <thead><tr><th>HTTP</th><th>When</th></tr></thead>
                                <tbody>
                                    <tr><td><code>200</code></td><td>Status returned</td></tr>
                                    <tr><td><code>401</code></td><td>Missing/invalid API key</td></tr>
                                    <tr><td><code>404</code></td><td>No transaction with that reference for this merchant</td></tr>
                                </tbody>
                            </table>
                        </section>

                        <Flourish />

                        {/* BVN VERIFICATION */}
                        <section className="wq-section" id={contents[5]}>
                            <h2 className="wq-h2">BVN Verification</h2>
                            <p className="wq-lead">
                                Verify a customer's Bank Verification Number (KYC). Verification takes two steps: submitting the BVN sends an OTP to the phone number registered against it, and the holder's record is only returned once that OTP is sent back. A BVN can't be looked up from the number alone.
                            </p>

                            <Endpoint method="POST" path="/api/v2/identity-verification/bvn/initiate" />
                            <Endpoint method="POST" path="/api/v2/identity-verification/bvn/complete" />

                            {/* Step 1 */}
                            <h3 className="wq-h3">Step 1 — initiate</h3>
                            <h4 className="wq-h4">Request body</h4>
                            <table className="wq-table">
                                <thead><tr><th>Field</th><th>Type</th><th>Notes</th></tr></thead>
                                <tbody>
                                    <tr><td><code>bvn</code><Required /></td><td className="wq-type">string</td><td>Exactly 11 digits</td></tr>
                                </tbody>
                            </table>
                            {bvnInitiateRequest}
                            <h4 className="wq-h4">Example</h4>
                            <Tabs tabs={bvnInitiateTabs} />
                            <h4 className="wq-h4">Response</h4>
                            <p className="wq-p">
                                A <code>reference</code> and the masked phone number the OTP went to. No BVN details.
                            </p>
                            {bvnInitiateResponse}

                            {/* Step 2 */}
                            <h3 className="wq-h3">Step 2 — complete</h3>
                            <h4 className="wq-h4">Request body</h4>
                            <table className="wq-table">
                                <thead><tr><th>Field</th><th>Type</th><th>Notes</th></tr></thead>
                                <tbody>
                                    <tr><td><code>reference</code><Required /></td><td className="wq-type">string</td><td>The reference returned by step 1</td></tr>
                                    <tr><td><code>otp</code><Required /></td><td className="wq-type">string</td><td>The code sent to the BVN's registered phone</td></tr>
                                </tbody>
                            </table>
                            {bvnCompleteRequest}
                            <h4 className="wq-h4">Example</h4>
                            <Tabs tabs={bvnCompleteTabs} />
                            <h4 className="wq-h4">Response</h4>
                            <p className="wq-p">
                                Key fields; the full record adds demographics, enrollment bank and a base64 portrait — fields the provider doesn't return are omitted.
                            </p>
                            {bvnCompleteResponse}

                            {/* Test mode */}
                            <h3 className="wq-h3">Test mode</h3>
                            <p className="wq-p">
                                Test vs live is decided by your merchant account's mode, not by a request field.
                            </p>
                            <table className="wq-table">
                                <thead><tr><th>Merchant mode</th><th>BVN sent</th><th>Result</th></tr></thead>
                                <tbody>
                                    <tr><td>Test</td><td><code>22222222222</code></td><td>No SMS sent, OTP is always <code>123456</code>, a sandbox record returned</td></tr>
                                    <tr><td>Test</td><td>any other</td><td>400 — use the test BVN</td></tr>
                                    <tr><td>Live</td><td><code>22222222222</code></td><td>400 — the test BVN can't be used in live mode</td></tr>
                                    <tr><td>Live</td><td>any other</td><td>Real lookup, real OTP to the BVN's phone (billable)</td></tr>
                                </tbody>
                            </table>
                            <p className="wq-p">
                                The sandbox record is easy to spot: <code>firstName: "Test"</code>, <code>lastName: "User"</code>, <code>watchListed: "SANDBOX"</code>.
                            </p>

                            {/* Errors */}
                            <h3 className="wq-h3">Errors</h3>
                            <table className="wq-table">
                                <thead><tr><th>HTTP</th><th>When</th></tr></thead>
                                <tbody>
                                    <tr><td><code>400</code></td><td>BVN not 11 digits; wrong BVN for your mode; OTP couldn't be sent; unknown or expired reference; wrong OTP (message says how many attempts remain)</td></tr>
                                    <tr><td><code>403</code></td><td>Too many wrong OTPs — the reference is locked; start again</td></tr>
                                    <tr><td><code>422</code></td><td>No phone number is registered against the BVN, so no OTP can be sent. Not retryable</td></tr>
                                </tbody>
                            </table>

                            {/* Integration notes */}
                            <h3 className="wq-h3">Integration notes</h3>
                            <ul className="wq-p" style={{ listStyle: 'disc', paddingLeft: '1.25rem' }}>
                                <li>References expire after 10 minutes or 5 wrong OTPs. To resend a code, call initiate again — don't keep retrying complete.</li>
                                <li>Complete is safe to retry with the same reference and OTP until it expires. Initiate is not: each call makes a new reference, may send another SMS, and in live mode may be billed.</li>
                                <li>Keep the reference on your server — never in a URL or browser storage.</li>
                                <li>Don't log or cache the step 2 response. It's full identity data including a photo. Log the reference instead.</li>
                            </ul>
                        </section>

                        <Flourish />

                        {/* WEBHOOK */}
                        <section className="wq-section" id={contents[6]}>
                            <h2 className="wq-h2">Transaction Webhook</h2>

                            <Note tone="critical" label="Critical">
                                Verify every webhook signature before acting on it. Without verification, anyone who learns your webhook URL can post fake "payment received" events. Treat an unsigned or wrongly-signed call as hostile and reject it.
                            </Note>

                            <p className="wq-lead">
                                WayaQuick calls your server when a payment succeeds or fails, so you can mark orders paid and trigger fulfilment in real time instead of polling.
                            </p>

                            <p className="wq-p">
                            You're integrating with this if you want to:
                            </p>

                            <ul className="wq-p" style={{listStyle: 'disc', paddingLeft: '1.25rem' }}>
                                <li>Mark an order paid in your own system without waiting for the customer to be redirected back.</li>
                                <li>Trigger fulfilment (release goods, send tickets, activate a subscription, etc.) the moment funds settle.</li>
                                <li>Keep your own records in step with WayaQuick without checking each payment by hand.</li>
                            </ul>

                            <p className="wq-p">
                            If you're polling <code>GET /api/v2/payment-collect/status/{'{refNo}'}</code> instead, you can use this webhook to remove the polling and react in real time. Keep the status endpoint as your safety net for reconciliation.
                            </p>

                            {/* Set up */}
                            <h3 className="wq-h3">Set up</h3>
                            <ol className="wq-p" style={{ listStyle: 'decimal', paddingLeft: '1.25rem' }}>
                                <li>Log in to your WayaQuick dashboard.</li>
                                <li>Open <strong>Settings → WayaQuick Settings → API Keys</strong>.</li>
                                <li>Set the Webhook URL to a publicly reachable HTTPS endpoint, e.g. <code>https://api.your-shop.com/waya/webhook</code>.</li>
                                <li>On the same tab, note your secret keys. You have one for TEST and one for PRODUCTION; both are needed for signature verification.</li>
                                <li>(Optional) Test the URL by initiating a small payment in TEST mode and confirming you receive the call.</li>
                            </ol>

                            {/* The request */}
                            <h3 className="wq-h3">The request</h3>
                            <p className="wq-p">
                                WayaQuick sends a single HTTP request whenever a transaction's state changes to <code>SUCCESSFUL</code> or <code>FAILED</code>:
                            </p>
                            {webhookRequestExample}
                            <h4 className="wq-h4">Body</h4>
                            {webhookBody}

                            <h4 className="wq-h4">Field reference</h4>
                            <Note label="Casing">
                            WayaQuick's contract evolved over time and the first seven fields use PascalCase (<code>OrderId</code>, <code>Amount</code>, …) while newer fields use camelCase (<code>customer</code>, <code>merchantId</code>, …). Honour the casing exactly, or map per field. Null fields are omitted from the JSON; treat absent fields as null, not as missing.
                            </Note>
                            <table className="wq-table">
                                <thead><tr><th>Field</th><th>Type</th><th>Always</th><th>Description</th></tr></thead>
                                <tbody>
                                    <tr><td><code>OrderId</code></td><td className="wq-type">string</td><td>yes</td><td>The transaction reference (refNo). Use this as your idempotency key.</td></tr>
                                    <tr><td><code>Amount</code></td><td className="wq-type">decimal</td><td>yes</td><td>Amount the customer was charged.</td></tr>
                                    <tr><td><code>Description</code></td><td className="wq-type">string</td><td>yes</td><td>The description supplied at checkout.</td></tr>
                                    <tr><td><code>Fee</code></td><td className="wq-type">decimal</td><td>yes</td><td>Processing fee deducted. Net to merchant = Amount minus Fee.</td></tr>
                                    <tr><td><code>Currency</code></td><td className="wq-type">string</td><td>yes</td><td>ISO currency code. Always NGN today.</td></tr>
                                    <tr><td><code>Status</code></td><td className="wq-type">string</td><td>yes</td><td>One of SUCCESSFUL, FAILED.</td></tr>
                                    <tr><td><code>TranTime</code></td><td className="wq-type">string</td><td>yes</td><td>Transaction time on the gateway. ISO-8601 local.</td></tr>
                                    <tr><td><code>TransactionDate</code></td><td className="wq-type">string</td><td>yes</td><td>Same instant, formatted "yyyy-MM-dd HH:mm:ss".</td></tr>
                                    <tr><td><code>productName</code></td><td className="wq-type">string</td><td>yes</td><td>Channel: CARD, WALLET, USSD, BANK, PAYATTITUDE.</td></tr>
                                    <tr><td><code>businessName</code></td><td className="wq-type">string</td><td>yes</td><td>Your business name as registered on WayaQuick.</td></tr>
                                    <tr><td><code>customer</code></td><td className="wq-type">object</td><td>yes</td><td>{'{ name, email, phoneNumber, customerId }'}.</td></tr>
                                    <tr><td><code>merchantId</code></td><td className="wq-type">string</td><td>yes</td><td>Your merchant ID. Same value for every webhook to your account.</td></tr>
                                    <tr><td><code>branchCategory</code></td><td className="wq-type">string | null</td><td>optional</td><td>The branch tag if you've configured one.</td></tr>
                                    <tr><td><code>recurrentPayment</code></td><td className="wq-type">boolean</td><td>yes</td><td>true for charges driven by a subscription / saved card.</td></tr>
                                </tbody>
                            </table>

                            {/* Verifying the signature */}
                            <h3 className="wq-h3">Verifying the signature</h3>
                            <p className="wq-lead">Verify every webhook before acting on it. Without verification anyone who learns your webhook URL can post fake "payment received" events.</p>
                            <p className="wq-p">The signature is:</p>
                            {webhookSignatureFormula}
                            <p className="wq-p">
                                <code>merchant_secret</code> is your <code>merchantSecretTestKey</code> for a TEST transaction, or your <code>merchantProductionSecretKey</code> for a PRODUCTION one. Most merchants keep one verifier per environment and route by which key validates.
                            </p>
                            <Note label="Raw body">
                                The signed payload uses the exact bytes we sent. If your framework deserialises and re-serialises the JSON (Express's default <code>express.json()</code> does), the recomputed HMAC will not match. Capture the raw request body (<code>express.raw()</code>, Flask <code>request.get_data()</code>, PHP <code>php://input</code>) before any JSON parsing.
                            </Note>
                            <Tabs tabs={webhookVerifyTabs} />

                            <h4 className="wq-h4">Defending against replay</h4>
                            <p className="wq-p">
                                The timestamp is part of what's signed, so an attacker can't forge a fresh timestamp against an old signature, but they can replay the same captured tuple. Reject anything outside a tolerance window, e.g. 5 minutes:
                            </p>
                            {webhookReplay}

                            {/* Status semantics */}
                            <h3 className="wq-h3">Status values &amp; semantics</h3>
                            <table className="wq-table">
                                <thead><tr><th>Status</th><th>What it means</th><th>What you should do</th></tr></thead>
                                <tbody>
                                    <tr><td><code>SUCCESSFUL</code></td><td>Customer paid the exact amount requested. Funds queued for settlement.</td><td>Fulfil the order. Check <code>OrderId</code> for idempotency.</td></tr>
                                    <tr><td><code>FAILED</code></td><td>Card declined, customer abandoned, or upstream rejected. Funds never moved.</td><td>No fulfilment. Optionally retry the customer journey.</td></tr>
                                </tbody>
                            </table>
                            <Note label="Exact amounts only">
                                Customers must pay the exact amount requested. A payment for a smaller or larger amount is rejected automatically, and if the funds have already come through they are reversed to the customer automatically. You will never receive a <code>SUCCESSFUL</code> webhook for a mismatched amount, so there is nothing to hold or reconcile on your side.
                            </Note>

                            {/* Idempotency & retries */}
                            <h3 className="wq-h3">Idempotency &amp; retries</h3>
                            <ul className="wq-p" style={{ listStyle: 'disc', paddingLeft: '1.25rem' }}>
                                <li>The same <code>OrderId</code> may be sent more than once (for example, a re-emitted SUCCESSFUL). Always upsert keyed by <code>OrderId</code>; never blindly insert.</li>
                                <li>Delivery is fire-and-forget today: a non-2xx response or downtime is not retried. Reconcile periodically with <code>GET /api/v2/payment-collect/status/{'{OrderId}'}</code>.</li>
                                <li>Respond with HTTP 200 within about 10 seconds. Only the status code is read; the body is ignored.</li>
                                <li>Don't do heavy work in the request thread. Acknowledge fast, then queue the actual fulfilment.</li>
                            </ul>
                            

                            {/* End-to-end example */}
                            <h3 className="wq-h3">End-to-end example</h3>
                            <p className="wq-p">A successful card payment of ₦1,500:</p>
                            {webhookE2E}

                            <h3 className="wq-h3">Testing</h3>
                            <ul className="wq-p" style={{ listStyle: 'disc', paddingLeft: '1.25rem' }}>
                                <li>Switch your dashboard to Test mode and use your test keys.</li>
                                <li>The cards on the <Link to="/test-card-information">Test Card Information</Link> page return fixed outcomes (success, decline, 3DS required).</li>
                                <li>The webhook payload is identical to live except productName may indicate test channels and the URL is your test webhook URL (configure separately in the dashboard if you want).</li>
                            </ul>
                            <p className="wq-p">If you don't see the webhook arrive:</p>
                            <ol className="wq-p" style={{ listStyle: 'decimal', paddingLeft: '1.25rem' }}>
                                <li>Confirm your URL is publicly reachable (we won't hit localhost). Use a tunnel (ngrok, cloudflared) for local dev.</li>
                                <li>Confirm your URL accepts a self-signed-cert-free HTTPS chain. We won't follow redirects.</li>
                                <li>Check the dashboard's webhook activity log for the response code we saw from your endpoint.</li>
                            </ol>

                            {/* Common pitfalls */}
                            <h3 className="wq-h3">Common pitfalls</h3>
                            <table className="wq-table">
                                <thead><tr><th>Symptom</th><th>Cause</th><th>Fix</th></tr></thead>
                                <tbody>
                                    <tr><td>Signature never matches</td><td>Framework re-serialised the JSON before the HMAC</td><td>Compute the HMAC against the raw request bytes</td></tr>
                                    <tr><td>Matches in TEST but not LIVE</td><td>Same secret used for both</td><td>Test secret for TEST, production secret for LIVE</td></tr>
                                    <tr><td>Some characters differ</td><td>Non-ASCII body decoded as Latin-1</td><td>Decode as UTF-8 end to end</td></tr>
                                    <tr><td>Duplicate orders</td><td>Insert on every webhook</td><td>Upsert keyed by <code>OrderId</code></td></tr>
                                    <tr><td>Stopped receiving after an outage</td><td>Endpoint 5xx'd; no retry</td><td>Reconcile via the collection status endpoint</td></tr>
                                    <tr><td>No signature header</td><td>Merchant has no production secret configured</td><td>Contact support; do not act on unsigned webhooks</td></tr>
                                </tbody>
                            </table>

                            <h3 className="wq-h3">Changelog</h3>
                            <table className="wq-table">
                                <thead><tr><th>Version</th><th>Date</th><th>Notes</th></tr></thead>
                                <tbody>
                                    <tr><td>1.0</td><td>2026-06-07</td><td>Initial spec. HMAC-SHA256 signature, env-specific secrets, fire-and-forget delivery. PARTIAL status supported for virtual-account flows.</td></tr>
                                    <tr><td>1.1</td><td>2026-10-26</td><td>PARTIAL status removed. Payments must match the requested amount; a smaller or larger payment is rejected and, if funds came through, reversed to the customer automatically.</td></tr>
                                    
                                </tbody>
                            </table>
                        </section>

                        <Flourish />

                        <Note label="Not included">
                            Intentionally omitted from this guide: standalone Account Activation, Create dynamic/static account, Transaction History, and Transaction Verify. Those endpoints still exist in the API but are out of scope for the standard Payout + Collection integration above.
                        </Note>

                        <footer className="wq-footer">
                            <p>WayaQuick Merchant API · Standard Integration · For dashboard access and key management visit your merchant portal.</p>
                        </footer>

                        {/* Previous / next nav, keeping your existing pattern */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-8">
                            <div className="w-full h-20 px-4 flex items-center justify-between border border-gray-300 rounded-lg">
                                <Link to="/sdks"><ArrowLeft /></Link>
                                <Link to="/sdks" className="flex flex-col">
                                    <p className="text-sm text-gray-500 text-right">
                                        <span className="text-gray-700">Previous</span>
                                    </p>
                                    <h5 className="text-gray-700 text-md font-medium text-right">SDKs &amp; Plugins</h5>
                                </Link>
                            </div>
                            <div className="w-full h-20 px-4 flex items-center justify-between border border-gray-300 rounded-lg">
                                <Link to="/test-card-information" className="flex flex-col">
                                    <p className="text-sm text-gray-500"><span className="text-gray-700">Next</span></p>
                                    <h5 className="text-gray-700 text-md font-medium">Test card Information</h5>
                                </Link>
                                <Link to="/test-card-information"><ArrowRight /></Link>
                            </div>
                        </div>
                    </div>
                    <RightNav contents={contents} />
                </div>
            </div>
        </div>
    );
}

export default DirectWebIntegration;